import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import type {
  Blackout,
  BlackoutLevel,
  DocRef,
  LeaveCreate,
  LeaveTypeId,
} from '@lms/shared-types'
import { useConfetti } from '../components/primitives/Confetti'
import { Button } from '../components/primitives/Button'
import { Card } from '../components/primitives/Card'
import { Icon } from '../components/primitives/Icon'
import { Field } from '../components/primitives/Field'
import { FadeIn } from '../components/primitives/FadeIn'
import { AnimatedHeight } from '../components/primitives/AnimatedHeight'
import { useToast } from '../components/primitives/Toast'
import { ConfirmationDialog } from '../components/primitives/ConfirmationDialog'
import { NumberTicker } from '../components/primitives/NumberTicker'
import { AutoFilledTag } from '../components/primitives/AutoFilledTag'
import {
  clearApplyDraft,
  consumeApplyDraftResumeFlag,
  formatRelativeTime,
  loadApplyDraft,
  saveApplyDraft,
} from '../lib/apply-draft'
import { LeaveTypePicker } from '../components/employee/LeaveTypePicker'
import { HalfDayPicker, listWorkingDays } from '../components/employee/HalfDayPicker'
import type { HalfDayMap } from '../components/employee/HalfDayPicker'
import { fullDisabledDates, seedToFit } from '../lib/half-day-balance'
import { HalfDayRow } from '../components/employee/HalfDayRow'
import type { HalfDayValue } from '../components/employee/HalfDayRow'
import { RangeCalendar } from '../components/employee/RangeCalendar'
import {
  StagedDocumentPicker,
  type StagedFile,
} from '../components/employee/StagedDocumentPicker'
import { ApiError } from '../api/client'
import {
  getDocumentUrl,
  uploadDocument,
} from '../api/documents'
import { uploadErrorMessage } from '../lib/upload-error'
import {
  createLeave,
  previewLeave,
  rollbackLeave,
} from '../api/leaves'
import {
  deleteLeaveDraft,
  getLeaveDraft,
  upsertLeaveDraft,
  type LeaveDraft,
} from '../api/leave-drafts'
import { LeaveCheck } from '../components/employee/LeaveCheck'
import type { LeaveCheckItem } from '../components/employee/LeaveCheck'
import { getMyApproverChain, listMyBalances } from '../api/me'
import { useReplacementCap } from '../api/me-replacement-cap'
import { listPublicHolidays } from '../api/public-holidays'
import { listBlackouts } from '../api/blackouts'
import { useSession } from '../auth/auth-context'
import { useEffectiveEmployeeId } from '../components/shell/effective-employee'
import { useDebounce } from '../lib/use-debounce'
import { useViewport } from '../lib/use-viewport'
import { flags } from '../lib/feature-flags'
import {
  LEAVE_TYPE_DOC_REQUIRED,
  LEAVE_TYPE_FIXED_DURATION,
  LEAVE_TYPE_HALF_DAY_ALLOWED,
  LEAVE_TYPE_LABEL,
} from '../lib/leave-type-meta'
import { LEAVE_TYPE_STYLE } from '../lib/leave-type-palette'
import { computeBookingWindow } from '../lib/booking-window'
import {
  daysLeftAfterCap,
  fixedDurationNote,
  type FixedDurationPick,
} from '../lib/fixed-duration'
import { fmtDate } from '../lib/format'
import { getEmployee } from '../api/employees'
import { listLeaveTypes } from '../api/leave-types'
import type { LeaveTypeMetadata } from '../api/leave-types'

// The only content rule for a reason is that it isn't blank — a reason of
// any length can be submitted once it has some non-whitespace content. Must
// stay in sync with the reason validator in services/api/src/schemas/leave.py.

/** Fallback reason text when Smart Intake verifies a proof document but
 *  the vision pass returned no extractable summary line. The preferred
 *  source is the document itself — what the
 *  certificate actually states. This map only kicks in when the extraction
 *  can't pull a one-liner off the doc. Keyed by leave type. Each string
 *  is non-blank so it submits cleanly. Covered by
 *  ApplyLeavePage.reason-fallback.test. */
export const SMART_INTAKE_REASON_FALLBACK_BY_TYPE: Partial<Record<LeaveTypeId, string>> = {
  medical: 'Attended clinic for treatment, certificate verified',
  hospitalization: 'Hospital admission, supporting document verified',
  maternity: 'Antenatal appointment, doctor letter verified',
  paternity: 'Newborn child registration, birth document verified',
  compassionate: 'Bereavement in the family, supporting document verified',
  marriage: 'Wedding ceremony, marriage certificate verified',
}

function formatCapDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function evidenceLabelFor(type: LeaveTypeId): string {
  switch (type) {
    case 'medical':
    case 'hospitalization':
      return 'medical certificate'
    case 'marriage':
      return 'marriage certificate'
    case 'maternity':
      return 'maternity / pregnancy doctor letter'
    case 'compassionate':
      return 'death / hospitalisation notice'
    default:
      return 'supporting document'
  }
}

function capitaliseFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Balance-capped leave types — hard-capped and not fixed-duration, so the
 *  dates calendar limits the range to the remaining balance. Annual is
 *  included now that overflow-to-Unpaid is removed (2026-07-21): the calendar
 *  greys out any end date past the accrued Annual balance, mirroring the
 *  server's R9 `balance_exceeded` block (both read the same
 *  `compute_available(...).available`). Unpaid (no balance) and
 *  Maternity/Paternity (fixed) are excluded; Birthday is single-day so a span
 *  cap is moot. */
const SPAN_CAPPED_TYPES: ReadonlySet<LeaveTypeId> = new Set<LeaveTypeId>([
  'annual',
  'medical',
  'hospitalization',
  'compassionate',
  'marriage',
  'replacement',
])
/** Of the capped types, the event-bound ones counted in elapsed (calendar)
 *  days rather than working days (mirrors the api's _ELAPSED_DAY_TYPES).
 *  NOTE: "marriage" is intentionally excluded — the Family & Marriage quota is
 *  handled like annual leave (working days, half-day capable), not as an
 *  elapsed continuous block. Keep in sync with the api's _ELAPSED_DAY_TYPES. */
const SPAN_ELAPSED_TYPES: ReadonlySet<LeaveTypeId> = new Set<LeaveTypeId>([
  'hospitalization',
  'compassionate',
])

/** The single reason rule: a reason is required and must be non-blank.
 *  Content is otherwise unconstrained — no length floor, no denylist, no
 *  distinct-word check, no AI judge. Returns whether the trimmed reason is
 *  empty (and therefore can't be submitted). */
export function reasonMissing(raw: string): boolean {
  return raw.trim().length === 0
}

// Error-tier warning codes that do NOT block submission. Everything else at
// tier="error" disables submit (blackout_blocked, unpaid_not_allowed,
// balance_exceeded, date errors, …). Capacity is NOT here — it never reaches
// tier="error" anymore (capacity de-gated 2026-09-24; it emits the warning-tier
// `capacity_over`). Kept at module scope so the block-reason helper below and
// the component share one source of truth.
export const NON_BLOCKING_WARNING_CODES: ReadonlySet<string> = new Set([
  'emergency_poor_planning',
  'emergency_short_notice',
  'unpaid_escalation',
])

/** The message of the first blocking (error-tier, non-exempt) warning, or
 *  undefined when nothing blocks. Drives the reason shown next to the disabled
 *  submit button so it names the actual gate that fired (e.g. the blackout /
 *  unpaid / capacity block message) instead of a generic fallback. */
export function firstBlockingWarningMessage(
  warnings: readonly { tier: string; code: string; message: string }[],
): string | undefined {
  return warnings.find(
    (w) => w.tier === 'error' && !NON_BLOCKING_WARNING_CODES.has(w.code),
  )?.message
}

// Draft persistence (localStorage) lives in `../lib/apply-draft` so the
// MyLeave Hero card can surface "1 draft saved" without duplicating
// storage keys / version logic.

interface StepHeaderProps {
  label: string
  required?: boolean
  hint?: ReactNode
  /** Optional inline adornment shown right after the label (e.g. AutoFilledTag).
   *  Rendered before the right-aligned hint so the eyebrow reads as
   *  "Step N · Title · [AUTO-FILLED]". */
  adornment?: ReactNode
}

/** Eyebrow header used at the top of each Apply-page step card.
 *  Dedupes the inline "Step N · Title" + optional required tag + optional
 *  right-aligned hint block previously copy-pasted 3× in the render tree. */
function StepHeader({ label, required = false, hint, adornment }: StepHeaderProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: 10,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-2)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span>{label}</span>
        {required ? (
          <span
            aria-label="required"
            style={{
              color: 'var(--danger)',
              textTransform: 'none',
              letterSpacing: 0,
              fontWeight: 600,
            }}
          >
            *
          </span>
        ) : null}
        {adornment}
      </span>
      {hint ? (
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{hint}</span>
      ) : null}
    </div>
  )
}

interface InlineAlertProps {
  kind: 'danger' | 'warning'
  message: string
  testId?: string
}

/** Compact in-card alert used for client-side validation messages.
 *  Replaces 4 copies of the same inline-styled <div role="alert"> across
 *  the form (booking window, replacement cap, reason quality, doc mismatch). */
function InlineAlert({ kind, message, testId }: InlineAlertProps): JSX.Element {
  const bg =
    kind === 'danger' ? 'rgba(196, 109, 58, 0.12)' : 'rgba(184, 138, 42, 0.12)'
  const line =
    kind === 'danger' ? 'var(--danger-line, #c46d3a)' : 'var(--warning-line, #b88a2a)'
  const fg = kind === 'danger' ? 'var(--danger-fg, #5a3018)' : 'var(--warning-fg, #4a3a10)'
  return (
    <div
      role="alert"
      {...(testId ? { 'data-testid': testId } : {})}
      style={{
        marginTop: 6,
        padding: '8px 12px',
        borderRadius: 8,
        background: bg,
        border: `1px solid ${line}`,
        fontSize: 12,
        color: fg,
        lineHeight: 1.5,
        animation:
          'lms-fade-in var(--motion-duration-base) var(--motion-ease-out) both',
      }}
    >
      {message}
    </div>
  )
}

// ── ReviewRail ──────────────────────────────────────────────────────────────
// Right-hand sticky rail (desktop) / stacked footer (mobile) — single
// source of truth for the at-a-glance numbers + the primary action.
// Replaces the old SunkenSummaryCard + bottom action row.

interface ReviewRailStat {
  label: string
  value: number
  /** Optional secondary text (e.g. "of 15") displayed under the number. */
  hint?: string
}

type ApproverRole = 'manager' | 'hod' | 'director' | 'exec_director'

interface ApproverPathStep {
  /** Display name; falls back to the role label when unknown. */
  name: string
  /** Role label rendered as the subtitle. */
  role: ApproverRole | 'employee'
  /** Whether this step decides (true) or is only informed (false). */
  decides: boolean
  /** Optional qualifier appended to the role subtitle, e.g.
   *  "for endorsement" → the chip reads "MANAGER · FOR ENDORSEMENT".
   *  Used for the intermediate manager node on an escalated path, where
   *  the manager endorses the request upward rather than deciding it. */
  qualifier?: string
}

interface ReviewRailProps {
  stats: ReviewRailStat[]
  approverPath: ApproverPathStep[]
  approverNote?: { text: string; severity: 'advisory' | 'blocking' } | null
  canSubmit: boolean
  submitDisabledReason: string | undefined
  isSubmitting: boolean
  celebrating: boolean
  saveDraftLabel: string
  onSaveDraft: () => void
  onCancel: () => void
  /** When a saved draft exists, the rail surfaces a Discard link below
   *  the action row. Caller wires this to a confirm + delete flow. */
  onDiscardDraft?: () => void
  hasSavedDraft?: boolean
  isImpersonating: boolean
  /** When true, renders with dark-panel colour overrides matching the
   *  ILMU Leave Assistant chrome (transparent background, white text). */
  dark?: boolean
  /** When true, suppresses the REVIEW stats grid. Used when the ILMU
   *  narrative block already surfaces the key coverage signal above. */
  hideStats?: boolean
  /** When true, suppresses the Approver path section. Used when the
   *  approver path is rendered as a standalone sticky panel in the
   *  right column instead of inside the ReviewRail. */
  hideApproverPath?: boolean
  /** When true, the action controls render as a single right-aligned row
   *  (Save draft · Cancel · Submit) with no card chrome — a top-border
   *  divider only — so they sit as a footer *inside* the form card rather
   *  than as a standalone stacked card. */
  inlineActions?: boolean
}

const RAIL_DIVIDER_STYLE: CSSProperties = {
  border: 'none',
  borderTop: '1px solid var(--border)',
  margin: '18px 0',
  width: '100%',
}

const RAIL_DIVIDER_DARK_STYLE: CSSProperties = {
  border: 'none',
  borderTop: '1px solid rgba(255,255,255,0.08)',
  margin: '18px 0',
  width: '100%',
}

const RAIL_EYEBROW_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-2)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
}

const RAIL_EYEBROW_DARK_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'rgba(255,255,255,0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
}

const APPROVER_ROLE_LABEL: Record<ApproverPathStep['role'], string> = {
  employee: 'You',
  // The first rung is whoever the approval chain assigns, not necessarily
  // someone holding the Manager role, so it reads as the generic "Approver".
  manager: 'Approver',
  hod: 'HOD',
  director: 'Director',
  exec_director: 'Exec Director',
}

type EscalationRole = 'exec_director'

/** Only the planned, discretionary leaves — Annual, Unpaid, Birthday and
 *  Replacement, all booked at the employee's discretion — are gated and can
 *  escalate. Every event-driven / unplanned type (sick, hospitalization,
 *  compassionate, maternity, paternity, marriage) bypasses the blackout +
 *  capacity gates entirely and follows the standard manager-approval flow,
 *  never the Exec Director. (2026-07-23.) Mirrors _DISCRETIONARY_GATED_TYPES in
 *  services/api/src/domain/leave_rules.py. */
const _DISCRETIONARY_GATED_TYPES: ReadonlySet<string> = new Set([
  'annual',
  'unpaid',
  'birthday',
  'replacement',
])

/** A leave escalates to the Executive Director when it is a literal `unpaid`
 *  type, or when it is a discretionary type (Annual/Unpaid) that overlaps a
 *  blackout. Non-discretionary types never escalate — they bypass the gate and
 *  take the standard manager flow. Returns `'exec_director'` when escalating, or
 *  null for the normal manager-approval path. (Whether an escalation is actually
 *  reachable vs hard-blocked is decided separately by the gate flags — see the
 *  `isBlocked` suppression at the call site.) */
export function _resolveEscalationRole(
  leaveType: string,
  blackout: { level: string } | null | undefined,
): EscalationRole | null {
  if (!_DISCRETIONARY_GATED_TYPES.has(leaveType)) return null
  const escalates = leaveType === 'unpaid' || blackout != null
  return escalates ? 'exec_director' : null
}

const _BLACKOUT_LEVEL_RANK: Record<BlackoutLevel, number> = {
  head: 1,
  director: 2,
  exec_director: 3,
}

/** Pick the highest-level blackout that overlaps `[startDate, endDate]` AND
 *  applies to the *applicant's own team*, returning its name + level (or null).
 *
 *  This drives the applicant's own approver-path routing, so it mirrors the
 *  server escalation rule exactly (services/api/src/domain/escalation.py::
 *  `_overlaps_active_blackout`): a blackout applies iff it is company-wide
 *  (empty `teamIds`) or its `teamIds` include the applicant's team. Role is
 *  irrelevant here — an HR applicant filing their *own* leave routes by their
 *  own team just like anyone else. (HR still *sees* every blackout on the
 *  calendar; that awareness is a separate concern — see `blackoutDates`.)
 *  Dates are ISO `YYYY-MM-DD`, so lexicographic comparison is a valid ordering.
 */
export function _pickOverlappingBlackout(
  blackouts: readonly Blackout[],
  args: { startDate: string; endDate: string; teamId: string | null | undefined },
): { name: string; level: BlackoutLevel } | null {
  const { startDate, endDate, teamId } = args
  let best: { name: string; level: BlackoutLevel } | null = null
  for (const b of blackouts) {
    // Team scope: a team-targeted blackout only applies to its own teams.
    if (b.teamIds.length > 0 && !(teamId && b.teamIds.includes(teamId))) continue
    // Date overlap.
    if (b.endDate < startDate || b.startDate > endDate) continue
    if (best === null || _BLACKOUT_LEVEL_RANK[b.level] > _BLACKOUT_LEVEL_RANK[best.level]) {
      best = { name: b.name, level: b.level }
    }
  }
  return best
}

interface ApplyProgressBarProps {
  /** Number of required sections satisfied (type, dates, reason). */
  completed: number
  /** Total required sections. */
  total: number
}

/**
 * Progress-as-border for the "New request" header. Rather than adding a
 * separate bar line, this renders the header's bottom border itself as the
 * completion track — a 1px full-width rule that colours in from the left as
 * the required inputs get satisfied, turning green once every step is done.
 * The caller decides how many steps there are: the universal three (leave
 * type → dates → reason) plus a document-upload step only when that leave
 * type requires proof, so a normal leave fills at 3/3 while a doc-required
 * leave can't read full until the upload lands. The host header must be
 * `position: relative`; this element pins to its bottom edge and replaces
 * the plain border. Width eases so the fill grows rather than snaps.
 */
function ApplyProgressBar({ completed, total }: ApplyProgressBarProps): JSX.Element {
  const ratio = total > 0 ? Math.min(1, Math.max(0, completed / total)) : 0
  const done = completed >= total && total > 0
  return (
    <div
      data-testid="apply-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={completed}
      aria-label="Leave request completion"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 1,
        background: 'var(--border)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${ratio * 100}%`,
          background: done ? 'var(--success-fg)' : 'var(--accent)',
          transition: 'width 220ms ease, background 220ms ease',
        }}
      />
    </div>
  )
}

function ReviewRail({
  stats,
  approverPath,
  approverNote,
  canSubmit,
  submitDisabledReason,
  isSubmitting,
  celebrating,
  saveDraftLabel,
  onSaveDraft,
  onCancel,
  onDiscardDraft,
  hasSavedDraft = false,
  isImpersonating,
  dark = false,
  hideStats = false,
  hideApproverPath = false,
  inlineActions = false,
}: ReviewRailProps): JSX.Element {
  return (
    <div
      data-testid="apply-review-rail"
      style={
        inlineActions
          ? {
              // Footer inside the form card: divider on top, no side border,
              // no card background — it belongs to the card above it.
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              padding: '16px 18px',
              borderTop: '1px solid var(--border)',
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              padding: 18,
              borderRadius: dark ? 0 : 12,
              background: dark ? 'transparent' : 'var(--surface)',
              border: dark ? 'none' : '1px solid var(--border)',
            }
      }
    >
      {/* Section 1 — Review stats (hidden when ILMU narrative is the primary signal) */}
      {!hideStats ? (
        <>
          <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <span style={dark ? RAIL_EYEBROW_DARK_STYLE : RAIL_EYEBROW_STYLE}>Review</span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 12,
                rowGap: 14,
              }}
            >
              {stats.map((s) => (
                <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: dark ? 'rgba(255,255,255,0.5)' : 'var(--text-3)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {s.label}
                  </span>
                  <span
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: dark ? 'white' : 'var(--text)',
                      lineHeight: 1.1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <NumberTicker
                      value={s.value}
                      decimals={Number.isInteger(s.value) ? 0 : 1}
                      animateOnChange
                      durationMs={400}
                    />
                  </span>
                  {s.hint ? (
                    <span style={{ fontSize: 11, color: dark ? 'rgba(255,255,255,0.5)' : 'var(--text-3)' }}>{s.hint}</span>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
          <hr style={dark ? RAIL_DIVIDER_DARK_STYLE : RAIL_DIVIDER_STYLE} aria-hidden="true" />
        </>
      ) : null}

      {/* Section 2 — Approver path */}
      {!hideApproverPath ? (
        <>
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={dark ? RAIL_EYEBROW_DARK_STYLE : RAIL_EYEBROW_STYLE}>Approver path</span>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                padding: '2px 0',
              }}
            >
              {approverPath.map((step, idx) => (
                <div
                  key={`${step.role}-${idx}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <ApproverChip
                    name={step.name || APPROVER_ROLE_LABEL[step.role]}
                    role={step.role}
                    decides={step.decides}
                    {...(step.qualifier ? { qualifier: step.qualifier } : {})}
                    dark={dark}
                  />
                  {idx < approverPath.length - 1 ? (
                    <span
                      aria-hidden="true"
                      style={{ color: dark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)', fontSize: 14 }}
                    >
                      →
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
            <span
              style={{
                fontSize: 11,
                color: dark ? 'rgba(255,255,255,0.5)' : 'var(--text-3)',
                lineHeight: 1.45,
                textAlign: 'center',
              }}
            >
              {approverNote ? <span>{approverNote.text}</span> : null}
            </span>
          </section>

          <hr style={dark ? RAIL_DIVIDER_DARK_STYLE : RAIL_DIVIDER_STYLE} aria-hidden="true" />
        </>
      ) : null}

      {/* Section 3 — Actions.
          Row layout (inlineActions): a single right-aligned footer row —
          Save draft · Discard · Cancel · Submit — reading secondary →
          primary left to right, clustered at the bottom-right of the card. */}
      {inlineActions ? (
        <section
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            data-testid="save-draft"
            onClick={onSaveDraft}
            disabled={isImpersonating || isSubmitting}
            style={{
              padding: '4px 6px',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-3)',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: 'inherit',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              cursor: isImpersonating || isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isImpersonating || isSubmitting ? 0.55 : 1,
            }}
          >
            {saveDraftLabel}
          </button>
          {hasSavedDraft && onDiscardDraft ? (
            <button
              type="button"
              data-testid="discard-draft-rail"
              onClick={onDiscardDraft}
              disabled={isSubmitting}
              style={{
                padding: '4px 6px',
                background: 'transparent',
                border: 'none',
                color: 'var(--danger-fg)',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'inherit',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.55 : 1,
              }}
            >
              Discard saved draft
            </button>
          ) : null}
          <button
            type="button"
            data-testid="cancel-leave"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              padding: '6px 4px',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-2)',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.55 : 1,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Cancel
          </button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            data-testid="submit-leave"
            data-celebrating={celebrating ? 'true' : 'false'}
            disabled={!canSubmit}
            loading={isSubmitting}
            {...(submitDisabledReason ? { disabledReason: submitDisabledReason } : {})}
          >
            {celebrating ? '✓ Submitted' : 'Submit request'}
          </Button>
        </section>
      ) : (
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          data-testid="submit-leave"
          data-celebrating={celebrating ? 'true' : 'false'}
          disabled={!canSubmit}
          loading={isSubmitting}
          {...(submitDisabledReason ? { disabledReason: submitDisabledReason } : {})}
          style={{ width: '100%' }}
        >
          {celebrating ? '✓ Submitted' : 'Submit request'}
        </Button>
        {/* Prominent secondary — back out of the application entirely.
            Drafts persist regardless, so this is non-destructive. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            data-testid="cancel-leave"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              padding: '6px 4px',
              background: 'transparent',
              border: 'none',
              color: dark ? 'rgba(255,255,255,0.75)' : 'var(--text-2)',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.55 : 1,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Cancel
          </button>
        </div>
        {/* Mini-link tray — Save draft status + the rarely-used Discard.
            Both stay small so they don't compete with Cancel above. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            data-testid="save-draft"
            onClick={onSaveDraft}
            disabled={isImpersonating || isSubmitting}
            style={{
              padding: '4px 6px',
              background: 'transparent',
              border: 'none',
              color: dark ? 'rgba(255,255,255,0.45)' : 'var(--text-3)',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: 'inherit',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              cursor: isImpersonating || isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isImpersonating || isSubmitting ? 0.55 : 1,
            }}
          >
            {saveDraftLabel}
          </button>
          {hasSavedDraft && onDiscardDraft ? (
            <>
              <span aria-hidden="true" style={{ color: dark ? 'rgba(255,255,255,0.2)' : 'var(--border-strong)', fontSize: 11 }}>·</span>
              <button
                type="button"
                data-testid="discard-draft-rail"
                onClick={onDiscardDraft}
                disabled={isSubmitting}
                style={{
                  padding: '4px 6px',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--danger-fg)',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.55 : 1,
                }}
              >
                Discard saved draft
              </button>
            </>
          ) : null}
        </div>
      </section>
      )}
    </div>
  )
}

interface ApproverChipProps {
  name: string
  role: ApproverPathStep['role']
  decides: boolean
  qualifier?: string
  dark?: boolean
}

function ApproverChip({ name, role, decides, qualifier, dark = false }: ApproverChipProps): JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
  const isEmployee = role === 'employee'
  // Role label sits INLINE after the name (not stacked). The requester chip
  // has no role — they're the origin, not an approval node. An optional
  // qualifier ("for endorsement") appends to the role so an intermediate
  // manager reads "MANAGER · FOR ENDORSEMENT".
  const roleLabel = isEmployee
    ? null
    : qualifier
      ? `${APPROVER_ROLE_LABEL[role]} · ${qualifier}`
      : APPROVER_ROLE_LABEL[role]
  // Filled avatar: the accent for whoever decides, neutral grey for You /
  // informed rungs. The decider chip also gets an accent-tinted border so the
  // "who signs off" node reads first.
  const avatarBg = decides ? 'var(--steel-blue)' : 'var(--text-3)'
  const chipBorder = decides
    ? 'color-mix(in srgb, var(--steel-blue) 42%, var(--border))'
    : dark
      ? 'rgba(255,255,255,0.15)'
      : 'var(--border)'
  return (
    <span
      data-role={role}
      data-decides={decides ? 'true' : 'false'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 11px 5px 6px',
        borderRadius: 999,
        background: dark ? 'rgba(255,255,255,0.06)' : 'var(--surface)',
        border: `1px solid ${chipBorder}`,
        fontSize: 12.5,
        fontWeight: 600,
        color: dark ? 'white' : 'var(--text)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 999,
          background: avatarBg,
          color: 'white',
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.02em',
          flex: 'none',
        }}
      >
        {initials || '·'}
      </span>
      <span>{name}</span>
      {roleLabel ? (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: dark ? 'rgba(255,255,255,0.55)' : 'var(--text-3)',
          }}
        >
          {roleLabel}
        </span>
      ) : null}
    </span>
  )
}

// ── ApproverPathPanel ───────────────────────────────────────────────────────
//
// Standalone light card rendered in the right column below the ILMU Leave
// Assistant. Deliberately NOT styled like the dark ILMU panel: the approver
// path is a deterministic routing fact (who the request goes to), not an
// AI-generated suggestion. Keeping it on a plain white surface prevents
// employees from misreading it as ILMU output.

interface ApproverPathPanelProps {
  approverPath: ApproverPathStep[]
  approverNote?: { text: string; severity: 'advisory' | 'blocking' } | null
}

function ApproverPathPanel({
  approverPath,
  approverNote,
}: ApproverPathPanelProps): JSX.Element {
  return (
    <div
      data-testid="approver-path-panel"
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        color: 'var(--text)',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 18px',
        }}
      >
        <span style={RAIL_EYEBROW_STYLE}>Approver path</span>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            padding: '2px 0',
          }}
        >
          {approverPath.map((step, idx) => (
            <div
              key={`${step.role}-${idx}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <ApproverChip
                name={step.name || APPROVER_ROLE_LABEL[step.role]}
                role={step.role}
                decides={step.decides}
                {...(step.qualifier ? { qualifier: step.qualifier } : {})}
              />
              {idx < approverPath.length - 1 ? (
                <span
                  aria-hidden="true"
                  style={{ color: 'var(--text-3)', fontSize: 14 }}
                >
                  →
                </span>
              ) : null}
            </div>
          ))}
        </div>
        {approverNote ? (
          // Escalation note — why this routes past the manager (unpaid /
          // blackout). Rendered as a △ heads-up (amber) with a top divider so
          // it reads as a reason, not a decoration. This is the one place that
          // owns "who decides"; the Before-you-submit checklist stays
          // facts-only and never restates the escalation.
          <div
            data-testid="approver-note"
            style={{
              marginTop: 2,
              paddingTop: 10,
              borderTop: '1px solid var(--border)',
              display: 'grid',
              gridTemplateColumns: '16px 1fr',
              gap: 8,
              alignItems: 'start',
            }}
          >
            <svg
              viewBox="0 0 20 20"
              width={16}
              height={16}
              fill="none"
              stroke="var(--warning)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ marginTop: 1, flex: 'none' }}
            >
              <path d="M10 2.5l7.5 13.5H2.5z" />
              <path d="M10 8v3.5M10 14h.01" />
            </svg>
            <span style={{ fontSize: 11.5, color: 'var(--warning-fg)', lineHeight: 1.45 }}>
              {approverNote.text}
            </span>
          </div>
        ) : (
          // Calm state — the request just goes to the manager. Muted so it
          // recedes; the chips above already name them.
          <div
            data-testid="approver-note-calm"
            style={{
              marginTop: 2,
              paddingTop: 10,
              borderTop: '1px solid var(--border)',
              fontSize: 11.5,
              color: 'var(--text-3)',
              lineHeight: 1.45,
            }}
          >
            Goes to your approver for approval.
          </div>
        )}
      </div>
    </div>
  )
}

interface FormState {
  /** Empty string until the employee picks a type — the form ships with no
   *  default so the leave-type choice is always intentional (and the phantom
   *  "-13.5 days · unpaid" that a defaulted Annual type produced is gone).
   *  Every consumer that indexes a per-type lookup or submits must guard on
   *  a non-empty value; `canSubmit` blocks submission while it is empty. */
  type: LeaveTypeId | ''
  startDate: string
  endDate: string
  reason: string
  startHalf: HalfDayValue
  endHalf: HalfDayValue
}

const INITIAL: FormState = {
  type: '',
  startDate: '',
  endDate: '',
  reason: '',
  startHalf: 'full',
  endHalf: 'full',
}

/** Whitelist of leave types accepted as `?type=` prefill. Mirrors
 *  `leaveTypeIdSchema` in shared-types, minus `unpaid` — unpaid is not a
 *  deep-link prefill target. */
const URL_PREFILL_TYPES: ReadonlyArray<LeaveTypeId> = [
  'annual',
  'medical',
  'hospitalization',
  'birthday',
  'compassionate',
  'maternity',
  'paternity',
  'marriage',
  'replacement',
]

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Reads `?type=&start=&end=` from `window.location.search` and returns a
 *  partial FormState. Called once during `useState(() => …)` so the form
 *  paints with the prefill applied; the URL is stripped of these params in
 *  a follow-up effect so reloads don't keep re-prefilling. */
function readUrlPrefill(): Partial<FormState> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const next: Partial<FormState> = {}
  const rawType = params.get('type')
  if (rawType && (URL_PREFILL_TYPES as ReadonlyArray<string>).includes(rawType)) {
    // `unpaid` is excluded from URL_PREFILL_TYPES above, so a `?type=unpaid`
    // link is already dropped here.
    next.type = rawType as LeaveTypeId
  }
  const start = params.get('start')
  if (start && ISO_DATE_RE.test(start)) next.startDate = start
  const end = params.get('end')
  if (end && ISO_DATE_RE.test(end)) next.endDate = end
  return next
}

/**
 * Storybook-only escape hatch. Lets a story pre-fill the form so the rest of
 * the page (preview query, impact card, summary card) renders without the
 * user needing to click through type + date pickers. Real callers omit this.
 */
export interface ApplyLeavePageStoryProps {
  __storybookInitialDraft?: Partial<FormState>
}

/**
 * Apply-leave redesign mirroring `new_reference/surface-employee.jsx`. Three
 * steps presented as stacked cards on a single column: Step 1 collapsed
 * type, Step 2 dates with two-month range calendar + START/END half-day
 * rows, Step 3 reason. Below the steps a sunken Summary card surfaces the
 * 4-stat at-a-glance grid, then an action row hosts the Save draft +
 * Submit request buttons.
 *
 * The submit mutation, queries, and warning list mirror v1 byte-identically
 * so the resulting `POST /api/v1/leaves` payload is unchanged.
 */
export function ApplyLeavePage({
  __storybookInitialDraft,
}: ApplyLeavePageStoryProps = {}): JSX.Element {
  const { user } = useSession()
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const navigate = useNavigate()
  const { isDesktop } = useViewport()
  const realId = user?.id ?? null
  const effectiveId = useEffectiveEmployeeId(realId)
  const isImpersonating = effectiveId !== realId

  const [form, setForm] = useState<FormState>(() => ({
    ...INITIAL,
    ...readUrlPrefill(),
    ...__storybookInitialDraft,
  }))
  // Dates are picked in a popover behind a "Set dates" trigger (Apply-form
  // redesign) — collapsing the two-month calendar keeps the form narrow.
  // The popover opens on click, closes on Escape / outside-click / Done, and
  // auto-closes for single-day + fixed-duration types (one click completes).
  const [datesOpen, setDatesOpen] = useState<boolean>(false)
  // Armed when the popover is reopened onto an existing selection: the first
  // date click then starts a FRESH pick instead of extending the committed
  // range. Without this, reopening after a 1-day pick and clicking a later
  // date extends (old → new) rather than re-selecting. Cleared on that first
  // click, so subsequent clicks in the same session still build a range.
  const [datesReopenFreshPick, setDatesReopenFreshPick] = useState<boolean>(false)
  const datesPopoverRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!datesOpen) return
    const onPointer = (e: MouseEvent): void => {
      if (datesPopoverRef.current && !datesPopoverRef.current.contains(e.target as Node)) {
        setDatesOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDatesOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [datesOpen])
  /** Per-date half-day map — used when `flags.halfDayPerDate` is ON.
   *  Keys are `YYYY-MM-DD`, values are 'AM' | 'PM'. Absent = FULL day. */
  const [perDateHalfDays, setPerDateHalfDays] = useState<HalfDayMap>({})
  /** Files staged client-side before the leave row exists. After a
   *  successful submit, each `staged` file flushes via `uploadDocument`
   *  using the new leave id, transitioning through `uploading` →
   *  `uploaded` (or `error`). */
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])
  // The saved draft's leave id, when one exists. Save-draft uploads staged
  // files onto this row; submit passes it as fromDraftId so those documents
  // carry over to the real leave instead of being
  // cascade-deleted when the draft is removed.
  const [draftLeaveId, setDraftLeaveId] = useState<string | null>(null)
  const [celebrating, setCelebrating] = useState<boolean>(false)
  const celebrateTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [bookingWindowError, setBookingWindowError] = useState<string | null>(null)
  /** Inline error shown above the reason textarea when the server rejects
   *  the reason (e.g. below the min-length floor on a raw-API bypass). */
  const [reasonQualityError, setReasonQualityError] = useState<string | null>(null)
  /** Tracks which form fields are currently populated by an automated
   *  source (today: SmartPrefillChips; previously also OCR pre-fill from a staged
   *  document). Each flag is set when the auto-fill writes the field and
   *  cleared the moment the user manually edits it. Drives `<AutoFilledTag />`
   *  next to the relevant step header. Mirrors prototype/employee.jsx
   *  `autoFilledKeys`. */
  const [autoFilledKeys, setAutoFilledKeys] = useState<{
    type: boolean
    dates: boolean
    reason: boolean
  }>({ type: false, dates: false, reason: false })
  useEffect(() => {
    return () => {
      if (celebrateTimer.current) clearTimeout(celebrateTimer.current)
    }
  }, [])

  // Strip `?type=&start=&end=` from the URL after we've consumed them in
  // the initial form state. A reload would otherwise reapply the prefill
  // and stomp on whatever the user has edited.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    let touched = false
    for (const key of ['type', 'start', 'end']) {
      if (params.has(key)) {
        params.delete(key)
        touched = true
      }
    }
    if (touched) {
      const next = params.toString()
      const url = `${window.location.pathname}${next ? `?${next}` : ''}`
      window.history.replaceState(null, '', url)
    }
  }, [])

  const { pop: confettiPop, ConfettiPortal } = useConfetti()

  // Fire confetti when the user switches to Birthday leave type
  const prevTypeRef = useRef<string>(INITIAL.type)
  useEffect(() => {
    if (form.type === 'birthday' && prevTypeRef.current !== 'birthday') {
      confettiPop()
    }
    prevTypeRef.current = form.type
  }, [form.type, confettiPop])

  const year = new Date().getUTCFullYear()
  const balancesQuery = useQuery({
    queryKey: ['me', 'balances', effectiveId, year],
    queryFn: () => listMyBalances(year),
    enabled: Boolean(effectiveId) && !isImpersonating,
  })
  const holidaysQuery = useQuery({
    queryKey: ['public-holidays', year],
    queryFn: listPublicHolidays,
    enabled: Boolean(user),
  })
  const blackoutsQuery = useQuery({
    queryKey: ['blackouts'],
    queryFn: listBlackouts,
    staleTime: 5 * 60_000,
    enabled: Boolean(user),
  })

  // Booking-window enforcement queries (migration 0056, feature flag gated).
  // We fetch the effective employee profile (works for acting-for too via
  // getEmployee on the subject ID) and the leave type catalog to get
  // bookable_in_advance per type.
  const bookingWindowEnabled = flags.bookingWindowEnforcement
  // Fetched unconditionally now — the Apply rail wants the
  // employee's `reportsTo` so the manager chip can render BEFORE
  // the preview query fires (i.e. before dates are picked).
  const employeeProfileQuery = useQuery({
    queryKey: ['employee', effectiveId],
    queryFn: () => getEmployee(effectiveId!),
    enabled: Boolean(effectiveId),
    staleTime: 5 * 60_000,
  })

  // Approver chain — manager + HOD + director + exec director resolved
  // server-side in one round-trip. Used by the right-rail chip row so
  // every rung shows the actual person's name on first paint, before
  // any date is picked (the preview endpoint also returns manager_name,
  // but only after dates land — and HOD/director/exec director names
  // were never wired through preview at all). Direct
  // `getEmployee(reportsTo)` is HR-gated, hence the dedicated /me
  // endpoint.
  // Key includes form.type so the chain refetches when the requester
  // switches leave type — an annual-only EA cover should only surface
  // for annual requests, not medical. Phase 5 of the
  // approval-assignments hardening slice.
  // Fetch with the selected type, or Annual as a stand-in before one is
  // picked — the base manager is the same across types, so this lets the
  // rail name the actual manager ("You → Hafiz Ramli") on first paint rather
  // than a generic "Manager" until the employee selects a type. Type-specific
  // escalation still refetches once a real type is chosen.
  const approverChainType = form.type || 'annual'
  const approverChainQuery = useQuery({
    queryKey: ['approver-chain', effectiveId, approverChainType],
    queryFn: () => getMyApproverChain(approverChainType),
    enabled: Boolean(effectiveId),
    staleTime: 5 * 60_000,
  })
  const leaveTypesQuery = useQuery({
    queryKey: ['leave-types'],
    queryFn: listLeaveTypes,
    staleTime: 10 * 60_000,
    enabled: bookingWindowEnabled,
  })

  const bookingWindow = useMemo(() => {
    if (!bookingWindowEnabled) return null
    const emp = employeeProfileQuery.data
    const leaveType = leaveTypesQuery.data?.find((lt: LeaveTypeMetadata) => lt.id === form.type)
    if (!emp) return null
    return computeBookingWindow(new Date(), {
      contractEndDate: emp.contractEndDate ?? null,
      bookingWindowOverride: emp.bookingWindowOverride,
    }, {
      enforced: bookingWindowEnabled,
      bookableInAdvance: leaveType?.bookableInAdvance ?? true,
    })
  }, [
    bookingWindowEnabled,
    employeeProfileQuery.data,
    leaveTypesQuery.data,
    form.type,
  ])

  // No type selected yet → every per-type lookup falls back to its "not
  // applicable" value so the form renders a neutral empty state.
  const halfDayAllowed = form.type ? LEAVE_TYPE_HALF_DAY_ALLOWED[form.type] : false
  const docsRequired = form.type ? LEAVE_TYPE_DOC_REQUIRED[form.type] : false
  const isBirthday = form.type === 'birthday'
  /** Calendar min/max for Birthday leave — restricts selection to the
   *  employee's birth month in the current year. The rule engine already
   *  rejects out-of-window picks, but constraining the UI removes the
   *  failure path entirely (clicking June 15 when your birthday is in
   *  September just doesn't work — the date is greyed out). */
  const birthdayMonthBounds = useMemo<
    { min: string; max: string } | null
  >(() => {
    if (!isBirthday) return null
    const birthIso = employeeProfileQuery.data?.birthDate
    if (typeof birthIso !== 'string' || birthIso.length < 10) return null
    const month = birthIso.slice(5, 7) // 'MM'
    const year = new Date().getFullYear()
    // JS Date quirk: day=0 of the *next* month is the last day of the
    // current month — handles 28/29/30/31 transparently incl. leap years.
    const lastDay = new Date(year, parseInt(month, 10), 0).getDate()
    return {
      min: `${year}-${month}-01`,
      max: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
    }
  }, [isBirthday, employeeProfileQuery.data])
  // Birthday leave can only be taken within the employee's birth month. Once
  // the current month is past it, there's no valid date left this year — so
  // Birthday is disabled in the type dropdown (the balance-0 case is already
  // hidden there via the exhaustion filter). Computed regardless of the
  // current selection since the dropdown lists every type.
  const birthdayMonthPassed = useMemo<boolean>(() => {
    const birthIso = employeeProfileQuery.data?.birthDate
    if (typeof birthIso !== 'string' || birthIso.length < 10) return false
    const birthMonth = parseInt(birthIso.slice(5, 7), 10)
    return new Date().getMonth() + 1 > birthMonth
  }, [employeeProfileQuery.data])
  const unavailableLeaveTypes = useMemo<ReadonlySet<LeaveTypeId>>(
    () => (birthdayMonthPassed ? new Set<LeaveTypeId>(['birthday']) : new Set<LeaveTypeId>()),
    [birthdayMonthPassed],
  )
  const fixedDuration = form.type ? LEAVE_TYPE_FIXED_DURATION[form.type] : undefined
  const isFixedDuration = fixedDuration !== undefined
  const startDate = form.startDate
  const endDate = isBirthday ? form.startDate : form.endDate
  // Header progress-bar steps: the universal three (type → dates → reason)
  // plus a document-upload step, but ONLY when this leave type requires
  // proof. Keeping the total dynamic means a normal leave isn't penalised
  // for a step it never has (it fills at 3/3), while a doc-required leave
  // reads 3/4 until the upload lands — so the bar can't sit full while
  // Submit is still blocked waiting on the document.
  const progressSteps: boolean[] = [
    Boolean(form.type),
    Boolean(startDate && endDate),
    form.reason.trim().length > 0,
    ...(docsRequired ? [stagedFiles.length > 0] : []),
  ]
  const progressCompleted = progressSteps.filter(Boolean).length
  const progressTotal = progressSteps.length
  const publicHolidayDates = useMemo(
    () => (holidaysQuery.data ?? []).map((h) => h.date),
    [holidaysQuery.data],
  )
  const holidayNames = holidaysQuery.data ?? []

  const blackoutDates = useMemo(() => {
    const teamId = user?.teamId
    if (!teamId || !blackoutsQuery.data) return []
    // HR sees all blackouts regardless of team; all other roles see only
    // blackouts that apply to their team (or have no team restriction).
    const seeAll = user?.role === 'hr'
    const dates: { date: string; name: string }[] = []
    for (const b of blackoutsQuery.data) {
      if (!seeAll && b.teamIds.length > 0 && !b.teamIds.includes(teamId)) continue
      const start = new Date(b.startDate)
      const end = new Date(b.endDate)
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push({ date: d.toISOString().slice(0, 10), name: b.name })
      }
    }
    return dates
  }, [blackoutsQuery.data, user?.teamId, user?.role])

  const halfDayMap = useMemo(() => {
    const map: Record<string, 'am' | 'pm'> = {}
    if (halfDayAllowed && form.startDate && form.startHalf !== 'full') {
      map[form.startDate] = form.startHalf
    }
    if (
      halfDayAllowed &&
      form.endDate &&
      form.endDate !== form.startDate &&
      form.endHalf !== 'full'
    ) {
      map[form.endDate] = form.endHalf
    }
    return map
  }, [halfDayAllowed, form.startDate, form.endDate, form.startHalf, form.endHalf])

  const draft = useMemo<LeaveCreate | null>(() => {
    if (!effectiveId || !form.type || !startDate || !endDate) return null
    if (startDate > endDate) return null

    if (flags.halfDayPerDate && halfDayAllowed) {
      // Per-date mode: convert HalfDayMap to the halfDays array the BE expects.
      // The legacy halfStart/halfEnd booleans are derived on the BE from this list;
      // we still pass them for backward compat with any read-paths that haven't
      // been updated.
      const halfDays = Object.entries(perDateHalfDays).map(([date, slot]) => ({
        date,
        slot: slot as 'AM' | 'PM',
      }))
      const halfDates = new Set(halfDays.map((e) => e.date))
      return {
        employeeId: effectiveId,
        type: form.type,
        startDate,
        endDate,
        halfDay: halfDays.length > 0,
        halfStart: halfDates.has(startDate),
        halfEnd: halfDates.has(endDate),
        halfDays,
        isEmergency: false,
        reason: form.reason.trim().length > 0 ? form.reason.trim() : 'pending',
        expiry: null,
        ...(draftLeaveId ? { fromDraftId: draftLeaveId } : {}),
      }
    }

    // Legacy mode: start/end half-day rows only.
    const halfStart = halfDayAllowed && form.startHalf !== 'full'
    const halfEnd =
      halfDayAllowed &&
      form.endHalf !== 'full' &&
      form.endDate !== form.startDate
    return {
      employeeId: effectiveId,
      type: form.type,
      startDate,
      endDate,
      halfDay: halfDayAllowed && (halfStart || halfEnd),
      halfStart,
      halfEnd,
      isEmergency: false,
      reason: form.reason.trim().length > 0 ? form.reason.trim() : 'pending',
      expiry: null,
      ...(draftLeaveId ? { fromDraftId: draftLeaveId } : {}),
    }
  }, [effectiveId, startDate, endDate, form, halfDayAllowed, perDateHalfDays, draftLeaveId])

  // ── Impact preview decoupled from reason ──────────────────────────────────
  // The Impact preview cares about dates, type, and half-day selection —
  // NOT the user's reason text. The previous implementation derived
  // `previewDraft` from the `draft` useMemo, which itself rebuilt on
  // every reason keystroke (because it depends on the whole `form`
  // object). That made `previewDraft` a new reference on every keystroke
  // and tripped the ref-based `isDebouncing` check below, flipping the
  // card to a skeleton even though the queryKey hash was unchanged.
  //
  // Fix: build `previewDraft` from primitive form fields directly, with
  // a stable string key for `halfDays`. Reason changes never touch any
  // dep, so the memo result is referentially stable.
  const halfDaysKey = useMemo(
    () =>
      Object.entries(perDateHalfDays)
        .map(([d, s]) => `${d}:${s}`)
        .sort()
        .join(','),
    [perDateHalfDays],
  )

  const previewDraft = useMemo<LeaveCreate | null>(() => {
    if (!effectiveId || !form.type || !startDate || !endDate) return null
    if (startDate > endDate) return null

    if (flags.halfDayPerDate && halfDayAllowed) {
      const halfDays = Object.entries(perDateHalfDays).map(([date, slot]) => ({
        date,
        slot: slot as 'AM' | 'PM',
      }))
      const halfDates = new Set(halfDays.map((e) => e.date))
      return {
        employeeId: effectiveId,
        type: form.type,
        startDate,
        endDate,
        halfDay: halfDays.length > 0,
        halfStart: halfDates.has(startDate),
        halfEnd: halfDates.has(endDate),
        halfDays,
        isEmergency: false,
        reason: 'pending',
        expiry: null,
      }
    }

    const halfStart = halfDayAllowed && form.startHalf !== 'full'
    const halfEnd =
      halfDayAllowed &&
      form.endHalf !== 'full' &&
      form.endDate !== form.startDate
    return {
      employeeId: effectiveId,
      type: form.type,
      startDate,
      endDate,
      halfDay: halfDayAllowed && (halfStart || halfEnd),
      halfStart,
      halfEnd,
      isEmergency: false,
      reason: 'pending',
      expiry: null,
    }
    // Intentionally depend on primitive fields only. `form.reason` is
    // omitted so reason typing never invalidates this memo. `halfDaysKey`
    // is a string derived from `perDateHalfDays` and stays stable across
    // identical maps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveId,
    startDate,
    endDate,
    form.type,
    form.startHalf,
    form.endHalf,
    halfDayAllowed,
    halfDaysKey,
  ])

  const debouncedPreviewDraft = useDebounce(previewDraft, 300)
  const previewQuery = useQuery({
    queryKey: ['leaves', 'preview', debouncedPreviewDraft],
    queryFn: () => {
      if (!debouncedPreviewDraft) throw new Error('no draft')
      return previewLeave(debouncedPreviewDraft)
    },
    enabled: Boolean(debouncedPreviewDraft),
  })

  // ── Impact-card phase ─────────────────────────────────────────────────────
  // One reducer for the four card phases. `previewDraft` is now
  // referentially stable across reason changes (see comment above), so a
  // strict-equality check correctly returns `false` while typing in the
  // reason textarea — the card stays in 'content' instead of flashing
  // skeleton. When dates actually move, the memo returns a new reference
  // and the 300ms debounce window briefly shows 'loading'.
  const isDebouncing = previewDraft !== null && previewDraft !== debouncedPreviewDraft
  const impactPhase: 'idle' | 'loading' | 'content' | 'error' = !startDate || !endDate
    ? 'idle'
    : previewQuery.isError
      ? 'error'
      : isDebouncing || (previewQuery.isFetching && previewQuery.data === undefined)
        ? 'loading'
        : 'content'

  // Replacement leave: cap the picker at the employee's remaining grant.
  // Uses the shared hook (same query key as HeroBalanceCard) so the result
  // is already in cache on first render when the employee arrives from the
  // home dashboard. The hook is always enabled — the shared cache amortises
  // the cost.
  const replacementCapResult = useReplacementCap()
  const replacementCap = replacementCapResult.data ?? null

  // D81 follow-up (PM LT-22 "RL entitlement didn't appear on apply RL page"):
  // `/me/balances` stores `replacement.entitlement = 0` because the grant
  // lives in the separate `replacement_grants` table. Mirror the same fix
  // commit 8d8e7ad8 applied to the home tile — overlay the live grant cap
  // onto the balance row consumed by LeaveTypePicker / BalanceLabel so the
  // picker stops hiding "replacement" for employees with an active grant.
  const rawBalances = balancesQuery.data ?? []
  const balances = useMemo(() => {
    if (replacementCap === null) return rawBalances
    const remaining = Number(replacementCap.remainingDays)
    return rawBalances.map((b) =>
      b.leaveType === 'replacement'
        ? {
            ...b,
            entitlement: remaining,
            accruedEntitlement: remaining,
            available: remaining,
          }
        : b,
    )
  }, [rawBalances, replacementCap])
  const balance = balances.find((b) => b.leaveType === form.type)
  // Dates-calendar range cap for balance-limited types: grey out any end date
  // that would push the span past the remaining balance. When half-days are
  // allowed a fractional remainder still funds one more calendar day (taken as
  // a half), so the cap is ceil(remaining) — e.g. 1.5 days funds a 2-day span
  // (1 full + 1 half). Without half-days it floors. The half-day picker forces
  // the trailing day to a half (see requiredHalfDates).
  const spanCapDays = ((): number | undefined => {
    if (!form.type || isFixedDuration || isBirthday) return undefined
    if (!SPAN_CAPPED_TYPES.has(form.type)) return undefined
    const remaining =
      form.type === 'replacement'
        ? replacementCap
          ? Number(replacementCap.remainingDays)
          : undefined
        : balance
          ? Number(balance.available)
          : undefined
    if (remaining === undefined || !Number.isFinite(remaining) || remaining <= 0) {
      return undefined
    }
    return halfDayAllowed ? Math.ceil(remaining) : Math.floor(remaining)
  })()
  // A balance that funds at most one calendar day (≤ 1 day left) is single-
  // select: the user may pick ANY date, but can't range it. Without this a
  // 1-day pick blocks moving to a later single date (the later click reads as a
  // span-capped range extension). Excludes fixed-duration / birthday (already
  // single) and unpaid / non-balance types (no cap).
  const singleDayOnly =
    form.type !== '' &&
    !isFixedDuration &&
    !isBirthday &&
    SPAN_CAPPED_TYPES.has(form.type) &&
    balance !== undefined &&
    balance.available > 0 &&
    balance.available <= 1
  const spanCapElapsed = form.type ? SPAN_ELAPSED_TYPES.has(form.type) : false
  // A balance-backed type with nothing left (annual is the visible case — it's
  // never hidden from the picker) can't be booked: there is no span to pick.
  // Gate the dates trigger on this so the calendar never opens onto a
  // fully-greyed month. Unpaid/Birthday have no balance row, so they're exempt.
  const balanceExhausted =
    form.type !== '' &&
    SPAN_CAPPED_TYPES.has(form.type) &&
    balance !== undefined &&
    balance.available <= 0
  // The unpaid escape hatch only surfaces once the employee's paid Annual
  // balance is fully spent — unpaid is a last resort, not an alternative to
  // available paid leave. Undefined (still loading) reads as "has balance", so
  // the link stays hidden until we know.
  const annualExhausted =
    (balances.find((b) => b.leaveType === 'annual')?.available ?? 1) <= 0
  // Working days whose FULL button must be disabled given the CURRENT per-date
  // selection. A day can't be taken full if doing so would push the total past
  // the remaining balance — i.e. the other days already consume more than
  // `available - 1`. This is dynamic, not positional: with 1.5 days left over a
  // 2-day span, making day 1 a half frees day 2 to be full (0.5 + 1.0 = 1.5),
  // whereas the old floor()-and-slice rule hard-locked the trailing day
  // regardless of what you picked earlier. Drives HalfDayPicker's disabled-FULL
  // state; empty unless the type is balance-capped and allows half-days.
  const requiredHalfDates = useMemo<string[]>(() => {
    if (!form.type || !halfDayAllowed) return []
    if (!SPAN_CAPPED_TYPES.has(form.type)) return []
    if (!startDate || !endDate) return []
    const avail = balance ? Number(balance.available) : NaN
    const workingDays = listWorkingDays(startDate, endDate, publicHolidayDates)
    return fullDisabledDates(workingDays, perDateHalfDays, avail)
  }, [
    form.type,
    halfDayAllowed,
    startDate,
    endDate,
    balance,
    publicHolidayDates,
    perDateHalfDays,
  ])
  // Corrective seed: if the current selection exceeds the balance (e.g. the
  // range was just extended past what's left, so newly-added days default to
  // FULL), force trailing full days to AM until the total fits. Only ADDS
  // halves when over budget — a valid selection is left untouched, so this
  // never fights a user who legitimately flips a seeded half back to full.
  useEffect(() => {
    if (!flags.halfDayPerDate || !halfDayAllowed) return
    if (!form.type || !SPAN_CAPPED_TYPES.has(form.type)) return
    if (!startDate || !endDate) return
    const avail = balance ? Number(balance.available) : NaN
    if (!Number.isFinite(avail) || avail <= 0) return
    const workingDays = listWorkingDays(startDate, endDate, publicHolidayDates)
    setPerDateHalfDays((prev) => seedToFit(workingDays, prev, avail))
  }, [
    flags.halfDayPerDate,
    halfDayAllowed,
    form.type,
    startDate,
    endDate,
    balance,
    publicHolidayDates,
  ])
  const replacementExceedsCap =
    form.type === 'replacement' &&
    replacementCap !== null &&
    previewQuery.data !== undefined &&
    previewQuery.data.computedDays > replacementCap.remainingDays
  // D81 / TC-1.1-REPL-01: a grant expires on its `nearestExpiry`. Even if the
  // day-count cap is respected, end_date > nearestExpiry must hard-block at
  // submit (server-side validate_replacement_expiry mirrors this guard).
  const replacementAfterExpiry =
    form.type === 'replacement' &&
    replacementCap !== null &&
    replacementCap.nearestExpiry !== null &&
    form.endDate.length > 0 &&
    form.endDate > replacementCap.nearestExpiry



  async function flushStagedUploads(leaveId: string): Promise<{
    succeeded: number
    failed: number
  }> {
    // Upload anything that is not yet persisted to the server.
    const toUpload = stagedFiles.filter((f) => f.status === 'staged')
    let succeeded = 0
    let failed = 0
    for (const sf of toUpload) {
      setStagedFiles((prev) =>
        prev.map((f) => (f.id === sf.id ? { ...f, status: 'uploading' } : f)),
      )
      try {
        const doc = await uploadDocument(leaveId, sf.file)
        setStagedFiles((prev) =>
          prev.map((f) =>
            f.id === sf.id
              ? { ...f, status: 'uploaded', remoteId: doc.id }
              : f,
          ),
        )
        succeeded += 1

      } catch (err) {
        // Map status → friendly reason (e.g. 415 → "Unsupported file type…")
        // rather than the raw "Request failed with status 415" — the api raises
        // document errors as FastAPI HTTPException, which the client can't
        // unwrap into its {error} envelope.
        const msg = uploadErrorMessage(err)
        setStagedFiles((prev) =>
          prev.map((f) =>
            f.id === sf.id ? { ...f, status: 'error', error: msg } : f,
          ),
        )
        failed += 1
      }
    }
    void queryClient.invalidateQueries({ queryKey: ['leaves'] })
    return { succeeded, failed }
  }

  const submitMutation = useMutation({
    mutationFn: (input: LeaveCreate) => createLeave(input),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['leaves'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['shell'] })
      void queryClient.invalidateQueries({ queryKey: ['nav-counts'] })
      // Declaring success is DEFERRED until the submission is actually
      // complete. For a doc-required leave the row is created first and the
      // document uploaded second — if that upload (and its rollback) fail,
      // the leave was NOT submitted, so we must not flash "Leave submitted"
      // (and the ✓ celebration) up front only to contradict it a moment
      // later with a failure toast. finalizeSuccess() runs on a confirmed
      // -good submission: immediately when there's nothing to upload, or
      // after every staged file lands.
      const finalizeSuccess = () => {
        notify('Leave submitted', 'success')
        // Submission landed — clear the saved draft (both server + local
        // mirror) so the user doesn't get prompted to restore the leave
        // they just submitted on next visit.
        clearApplyDraft(effectiveId)
        void deleteLeaveDraft().catch(() => {
          /* swallow — best effort */
        })
        setSavedDraft(null)
        setDraftJustSavedAt(null)
        setDraftLeaveId(null)
        setCelebrating(true)
        if (celebrateTimer.current) clearTimeout(celebrateTimer.current)
        celebrateTimer.current = setTimeout(() => setCelebrating(false), 1400)
      }

      // If the user staged files (only relevant for doc-required types),
      // flush them sequentially against the new leave row, then bounce
      // to /my-leave — but ONLY if every upload succeeded. If any
      // upload failed the user stays on the page so they can see the
      // failed chip (clay 'Failed' badge) and retry by removing +
      // re-picking the file.
      // Same as flushStagedUploads — anything not yet persisted server-side.
      const hasStaged = stagedFiles.some((f) => f.status === 'staged')
      // Tripwire: if the leave type required docs but we have zero staged
      // files about to flush, the server-side reconciler (5-min job) will
      // catch it — but emit a structured warning *now* so the issue is loud
      // in the browser console and ready for telemetry capture.
      if (docsRequired && !hasStaged) {
        // eslint-disable-next-line no-console
        console.warn('leave.created_without_required_doc', {
          leaveId: result.leave.id,
          leaveType: form.type,
          stagedFileCount: stagedFiles.length,
          stagedFileStatuses: stagedFiles.map((f) => f.status),
        })
      }
      if (hasStaged) {
        void flushStagedUploads(result.leave.id).then(async ({ failed }) => {
          if (failed > 0) {
            // Roll back the leave so the user is not stuck with an orphan
            // pending row the manager can see. The api hard-deletes the
            // row, refunds the pending balance, and best-effort-cleans
            // any documents that did upload.
            try {
              await rollbackLeave(result.leave.id)
              void queryClient.invalidateQueries({ queryKey: ['leaves'] })
              void queryClient.invalidateQueries({ queryKey: ['me'] })
              void queryClient.invalidateQueries({ queryKey: ['requests'] })
              void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
              void queryClient.invalidateQueries({ queryKey: ['shell'] })
              // Keep the chips that actually FAILED in their 'error' state so
              // the user sees the red 'Failed' badge and reason (e.g. an
              // unsupported HEIC) and knows to remove + re-pick a valid file.
              // Reset only the non-error chips: any that uploaded were deleted
              // server-side by the rollback, so they must re-upload on the next
              // submit and belong back at 'staged'.
              setStagedFiles((prev) =>
                prev.map((f) =>
                  f.status === 'error'
                    ? f
                    : { id: f.id, file: f.file, status: 'staged' as const },
                ),
              )
              notify(
                `${failed} document${failed === 1 ? '' : 's'} failed to upload — leave was not submitted. Try again.`,
                'error',
              )
            } catch (rollbackErr) {
              // Rollback failed — leave the user on the page so they can
              // see the error chips and retry, or contact HR. We do NOT
              // navigate to the detail view because the leave is broken.
              const message =
                rollbackErr instanceof Error ? rollbackErr.message : 'Rollback failed'
              notify(
                `Upload failed and rollback also failed: ${message}. Please contact HR.`,
                'error',
              )
            }
            return
          }
          // All uploads succeeded — the submission is now truly complete.
          finalizeSuccess()
          // brief moment for the user to read the 'Uploaded' badges before
          // navigating to the leave detail view so they see status + the AI
          // advisor's verdict.
          setTimeout(() => {
            void navigate({
              to: '/leave',
              search: { id: result.leave.id },
            })
          }, 1500)
        })
      } else {
        // No document upload step — the leave is complete on create.
        finalizeSuccess()
        setTimeout(() => {
          void navigate({
            to: '/leave',
            search: { id: result.leave.id },
          })
        }, 1500)
        setForm(INITIAL)
        setStagedFiles([])
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 422) {
        const details = error.details as { code?: string; message?: string; data?: { warnings?: Array<{ code: string }> }; bookingWindowDetail?: { maxAllowedDate?: string } } | null | undefined
        const hasBookingWindowErr = details?.data?.warnings?.some((w) => w.code === 'booking_window_exceeded')
        if (hasBookingWindowErr) {
          const maxDate = details?.data && 'bookingWindowDetail' in details.data
            ? (details.data as { bookingWindowDetail?: { maxAllowedDate?: string } }).bookingWindowDetail?.maxAllowedDate
            : undefined
          setBookingWindowError(
            maxDate
              ? `Apply ahead is capped at ${fmtDate(maxDate)}. Talk to HR for an override.`
              : 'Advance booking cap exceeded. Talk to HR for an override.',
          )
          return
        }
        // FastAPI request-validation 422 — raw Pydantic shape with no
        // `{code}` envelope: { detail: [{ loc: ['body','reason'], msg: '...' }] }.
        // The reason field's min-length floor raises here, so surface it inline
        // above the reason textarea rather than as a generic "Submit failed
        // (422)" toast.
        const pydanticErrors = (
          error.details as { detail?: Array<{ loc?: unknown[]; msg?: string }> } | null
        )?.detail
        if (Array.isArray(pydanticErrors)) {
          const reasonErr = pydanticErrors.find(
            (e) => Array.isArray(e.loc) && e.loc.includes('reason'),
          )
          if (reasonErr && typeof reasonErr.msg === 'string') {
            setReasonQualityError(
              reasonErr.msg
                .replace(/^Value error,\s*/i, '')
                .replace(/^reason_\w+:\s*/i, ''),
            )
            return
          }
        }
      }
      const status = error instanceof ApiError ? ` (${error.status})` : ''
      const message = error instanceof Error ? error.message : 'Submit failed'
      notify(`Submit failed${status}: ${message}`, 'error')
    },
  })

  // Server-side preview warnings, with a client-side suppression:
  //  - ``documents_required`` (tier=warning): server doesn't know about
  //    files we've staged in memory. Once at least one file is staged
  //    we trust ``docsBlocks`` below to enforce the rule and drop the
  //    server's duplicate so the user isn't told to do something they
  //    already did.
  const previewWarnings = (previewQuery.data?.warnings ?? []).filter(
    (w) => !(w.code === 'documents_required' && stagedFiles.length > 0),
  )
  // Pattern signals are advisory only — they surface in the impact card so
  // the approver sees them, but they never gate submission.
  // Capacity is NO LONGER blocking (de-gated 2026-09-24): an over-capacity
  // request emits the warning-tier `capacity_over` and files through; the
  // manager decides at approval with an explicit over-capacity confirm.
  const blockingErrorMessage = firstBlockingWarningMessage(previewWarnings)
  const hasErr = blockingErrorMessage !== undefined
  // Emergency classification (Policy §1.1) — short-notice annual OR replacement
  // leave. Server-authoritative via the preview's is_emergency flag. Surfaced
  // as a short heads-up row below (just the label) so the employee sees the
  // flag before submitting; the balance consequence and Poor-Planning detail
  // are left to the approver path / pattern row, so this row stays terse and
  // doesn't duplicate them. Non-blocking, and NOT gated on leave type so
  // replacement emergencies show too.
  const isEmergencyDraft = previewQuery.data?.isEmergency ?? false
  // Local estimate used while the debounced preview is in flight, so the
  // Review rail responds immediately when the user toggles a half-day in
  // the per-date picker (PM feedback 2026-05-21 — rail was showing "1 day"
  // for a half-day request because the preview hadn't returned yet).
  // Best-effort only: counts weekdays in range minus PH dates minus
  // selected half-day slots × 0.5. Authoritative number is the preview's
  // computed_days from the rule engine.
  const localComputedDays = useMemo(() => {
    if (!startDate || !endDate) return 1
    const phSet = new Set(publicHolidayDates)
    let workingDays = 0
    const cur = new Date(startDate + 'T00:00:00')
    const last = new Date(endDate + 'T00:00:00')
    while (cur <= last) {
      const dow = cur.getDay()
      const iso = cur.toISOString().slice(0, 10)
      if (dow !== 0 && dow !== 6 && !phSet.has(iso)) workingDays += 1
      cur.setDate(cur.getDate() + 1)
    }
    if (workingDays === 0) return 0
    let halfCount = 0
    if (flags.halfDayPerDate && halfDayAllowed) {
      halfCount = Object.keys(perDateHalfDays).filter((d) => {
        const dt = new Date(d + 'T00:00:00')
        const dow = dt.getDay()
        return dow !== 0 && dow !== 6 && !phSet.has(d)
      }).length
    } else if (halfDayAllowed) {
      if (form.startHalf !== 'full') halfCount += 1
      if (form.endHalf !== 'full' && endDate !== startDate) halfCount += 1
    }
    if (workingDays === 1 && halfCount >= 1) return 0.5
    return Math.max(0, workingDays - halfCount * 0.5)
  }, [
    startDate,
    endDate,
    publicHolidayDates,
    halfDayAllowed,
    perDateHalfDays,
    form.startHalf,
    form.endHalf,
  ])

  const computedDays = previewQuery.data?.computedDays ?? localComputedDays

  // The Reason field is gated on a real date selection: it stays disabled
  // until both ends are picked AND the range has at least one counted day
  // (a weekend-only pick computes to 0). Writing a reason before choosing
  // dates is out of order and the reason would apply to nothing.
  const reasonLocked = !startDate || !endDate || computedDays <= 0

  // Summary shown on the "Set dates" trigger once a selection exists, so the
  // collapsed calendar still answers "what did I pick?". Dates render as
  // dd MMM yyyy; the working-day count is emphasised (bold) on the trigger.
  //  - single day → "08 Jun 2026 · 1 working day"
  //  - range      → "08 Jun 2026 - 11 Jun 2026 · 5 working days"
  //  - mid-range   → "08 Jun 2026 - …" (start picked, waiting on the end click)
  const datesRangeLabel = ((): string => {
    if (!startDate) return ''
    const startLabel = format(parseISO(startDate), 'dd MMM yyyy')
    if (!endDate) return `${startLabel} - …`
    if (endDate === startDate) return startLabel
    return `${startLabel} - ${format(parseISO(endDate), 'dd MMM yyyy')}`
  })()
  // Only shown once both ends are set (a complete selection). Null mid-range.
  const datesDaysLabel =
    startDate && endDate
      ? `${computedDays} ${computedDays === 1 ? 'working day' : 'working days'}`
      : null

  // The only reason rule: required + non-blank. No length floor, no AI
  // judge, no denylist — a typed reason of any content and length can be
  // submitted once it isn't empty.
  const reasonBlocks = reasonMissing(form.reason)
  // A file only satisfies "has a doc" when it's in a state that will actually
  // be flushed by flushStagedUploads (status in {staged, reviewing, uploaded}).
  // Files stuck in 'error' or 'uploading' do NOT count — otherwise a previous
  // upload error could pass the gate and produce a doc-less leave row server
  // side (see incident 2026-05-22: leave a57f0ddd... created without docs).
  const hasUploadableDoc = stagedFiles.some(
    (f) => f.status === 'staged' || f.status === 'uploaded',
  )
  const docsBlocks = docsRequired && !hasUploadableDoc

  // ── Async still-processing gate ───────────────────────────────────────────
  // Submit must wait until every async verdict has settled on the *current*
  // form values; otherwise a stale OK lets through what a fresh check would
  // have blocked (or vice-versa). Two signals:
  //   2. Impact preview — debouncing OR first-fetch with no data yet
  //      (refetches with prior data don't block; UI shows stale values
  //      gracefully via impactPhase).
  const previewPending =
    Boolean(previewDraft) &&
    (isDebouncing || (previewQuery.isFetching && previewQuery.data === undefined))
  const aiProcessing = previewPending

  const canSubmit =
    !isImpersonating &&
    Boolean(draft) &&
    !hasErr &&
    !reasonBlocks &&
    !docsBlocks &&
    !replacementExceedsCap &&
    !replacementAfterExpiry &&
    !aiProcessing &&
    !submitMutation.isPending

  // ── Save-draft (server-backed, localStorage as offline fallback) ──────────
  // Primary store: POST/GET/DELETE /api/v1/me/leave-draft. localStorage
  // mirrors the same shape so a transient API failure doesn't lose the
  // user's work — the next successful save promotes localStorage →
  // server and clears the local copy.
  //
  // Shape boundary: the API persists a partial Leave row (type, dates,
  // half-day bools, reason). The form's `startHalf`/`endHalf` enum and
  // the per-date map lose a bit of fidelity on the server round-trip —
  // an 'am' half-day round-trips as `halfStart=true` and is re-hydrated
  // back to 'am' on restore.
  type DraftSnapshot = {
    form: FormState
    perDateHalfDays: HalfDayMap
    savedAt: string
    /** Draft leave id — only present for server-backed drafts (not the
     *  localStorage fallback). Enables document re-hydration + fromDraftId. */
    leaveId?: string
    /** Documents already attached to the draft. */
    documents?: DocRef[]
  }

  const apiDraftToSnapshot = useCallback(
    (d: LeaveDraft): DraftSnapshot => ({
      form: {
        type: d.type,
        startDate: d.startDate,
        endDate: d.endDate,
        reason: d.reason,
        startHalf: d.halfStart ? 'am' : 'full',
        endHalf: d.halfEnd ? 'am' : 'full',
      },
      perDateHalfDays: {},
      savedAt: d.savedAt,
      leaveId: d.id,
      documents: d.documents,
    }),
    [],
  )

  // Re-hydrate a saved draft's uploaded documents back into staged files by
  // downloading the bytes and reconstructing real File objects (StagedFile
  // requires a File; this keeps the preview path working). Best-effort per
  // doc — a failed fetch is skipped.
  const hydrateStagedFromDocs = useCallback(
    async (leaveId: string, documents: DocRef[]): Promise<StagedFile[]> => {
      const out: StagedFile[] = []
      for (const doc of documents) {
        try {
          const resp = await fetch(getDocumentUrl(leaveId, doc.id), {
            credentials: 'include',
          })
          if (!resp.ok) continue
          const blob = await resp.blob()
          const file = new File([blob], doc.filename, { type: doc.mimeType })
          out.push({
            id: doc.id,
            file,
            status: 'uploaded',
            remoteId: doc.id,
          })
        } catch {
          // Skip a doc we couldn't fetch — the draft leave still holds it and
          // fromDraftId will adopt it on submit regardless.
        }
      }
      return out
    },
    [],
  )

  const [savedDraft, setSavedDraft] = useState<DraftSnapshot | null>(null)
  const [restoreDismissed, setRestoreDismissed] = useState<boolean>(false)
  const [draftJustSavedAt, setDraftJustSavedAt] = useState<string | null>(null)

  useEffect(() => {
    if (!effectiveId) return
    let cancelled = false
    async function load(): Promise<void> {
      let snapshot: DraftSnapshot | null = null
      try {
        const remote = await getLeaveDraft()
        if (remote) snapshot = apiDraftToSnapshot(remote)
      } catch {
        // API unreachable — fall back to localStorage. The next
        // successful save will promote whatever the user has now.
      }
      if (!snapshot) {
        const local = loadApplyDraft<FormState, HalfDayMap>(effectiveId)
        if (local) {
          snapshot = {
            form: local.form,
            perDateHalfDays: local.perDateHalfDays as HalfDayMap,
            savedAt: local.savedAt,
          }
        }
      }
      if (cancelled || !snapshot) return
      setSavedDraft(snapshot)
      // Auto-restore on explicit resume intent (chip click flag OR
      // ?resume=1 in the URL).
      let shouldAutoRestore = consumeApplyDraftResumeFlag()
      if (!shouldAutoRestore && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search)
        if (params.get('resume') === '1') {
          shouldAutoRestore = true
          params.delete('resume')
          const next = params.toString()
          const url = `${window.location.pathname}${next ? `?${next}` : ''}`
          window.history.replaceState(null, '', url)
        }
      }
      if (shouldAutoRestore) {
        setForm(snapshot.form)
        setPerDateHalfDays(snapshot.perDateHalfDays)
        setRestoreDismissed(true)
        if (snapshot.leaveId && snapshot.documents && snapshot.documents.length > 0) {
          const dLeaveId = snapshot.leaveId
          setDraftLeaveId(dLeaveId)
          const hydrated = await hydrateStagedFromDocs(dLeaveId, snapshot.documents)
          if (!cancelled && hydrated.length > 0) setStagedFiles(hydrated)
        }
        notify('Draft restored', 'success')
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [effectiveId, notify, apiDraftToSnapshot, hydrateStagedFromDocs])

  const [replaceDraftOpen, setReplaceDraftOpen] = useState<boolean>(false)

  const performSaveDraft = useCallback(async (): Promise<void> => {
    if (!effectiveId) return
    // Need at least a type + date range to write a draft row (Leave
    // table is NOT NULL on those columns). Show a friendly error
    // instead of relying on a 422.
    if (!form.type) {
      notify('Pick a leave type before saving a draft.', 'warning')
      return
    }
    if (!form.startDate || !form.endDate) {
      notify('Pick your dates before saving a draft.', 'warning')
      return
    }
    const halfDays = Object.entries(perDateHalfDays).map(([date, slot]) => ({
      date,
      slot: slot as 'AM' | 'PM',
    }))
    const halfStart =
      perDateHalfDays[form.startDate] !== undefined ||
      (halfDayAllowed && form.startHalf !== 'full')
    const halfEnd =
      perDateHalfDays[form.endDate] !== undefined ||
      (halfDayAllowed && form.endHalf !== 'full' && form.endDate !== form.startDate)
    try {
      const saved = await upsertLeaveDraft({
        type: form.type,
        startDate: form.startDate,
        endDate: form.endDate,
        halfDay: halfStart || halfEnd,
        halfStart,
        halfEnd,
        halfDays,
        isEmergency: false,
        reason: form.reason,
      })
      // Promotion succeeded — wipe localStorage so we don't read a
      // stale copy on next visit.
      clearApplyDraft(effectiveId)
      setDraftLeaveId(saved.id)
      // Persist not-yet-uploaded files onto the draft leave row so the
      // uploaded file survives on the saved draft. Best-effort per file; a
      // failure leaves it staged for the next save or submit to retry.
      const toUpload = stagedFiles.filter((f) => f.status === 'staged')
      for (const sf of toUpload) {
        try {
          const doc = await uploadDocument(saved.id, sf.file)
          setStagedFiles((prev) =>
            prev.map((f) =>
              f.id === sf.id ? { ...f, status: 'uploaded', remoteId: doc.id } : f,
            ),
          )
        } catch {
          // leave it staged — the next save (or submit) retries the upload
        }
      }
      setSavedDraft(apiDraftToSnapshot(saved))
      setDraftJustSavedAt(saved.savedAt)
      notify('Draft saved — pick this back up later.', 'success')
    } catch {
      const local = saveApplyDraft<FormState, HalfDayMap>(effectiveId, {
        form,
        perDateHalfDays,
      })
      if (local) {
        setSavedDraft({
          form: local.form,
          perDateHalfDays: local.perDateHalfDays as HalfDayMap,
          savedAt: local.savedAt,
        })
        setDraftJustSavedAt(local.savedAt)
        notify("Saved offline — we'll sync when you're back online.", 'warning')
      } else {
        notify("Couldn't save draft. Try again.", 'error')
      }
    }
  }, [
    effectiveId,
    form,
    perDateHalfDays,
    halfDayAllowed,
    notify,
    apiDraftToSnapshot,
    stagedFiles,
  ])

  /** Single-draft rule: if a saved draft already exists, the second save
   *  must explicitly confirm the overwrite (the existing draft is
   *  discarded). The two cases that bypass the prompt are (a) no prior
   *  draft and (b) the user just saved this same form moments ago — we
   *  consider that an idempotent re-save, not an overwrite. */
  const handleSaveDraft = useCallback((): void => {
    if (savedDraft && draftJustSavedAt === null) {
      setReplaceDraftOpen(true)
      return
    }
    void performSaveDraft()
  }, [savedDraft, draftJustSavedAt, performSaveDraft])

  const handleRestoreDraft = useCallback((): void => {
    if (!savedDraft) return
    setForm(savedDraft.form)
    setPerDateHalfDays(savedDraft.perDateHalfDays)
    setRestoreDismissed(true)
    // Re-hydrate the uploaded file(s) from the server draft.
    if (savedDraft.leaveId && savedDraft.documents && savedDraft.documents.length > 0) {
      const dLeaveId = savedDraft.leaveId
      setDraftLeaveId(dLeaveId)
      void hydrateStagedFromDocs(dLeaveId, savedDraft.documents).then((hydrated) => {
        if (hydrated.length > 0) setStagedFiles(hydrated)
      })
    }
    notify('Draft restored', 'success')
  }, [savedDraft, notify, hydrateStagedFromDocs])

  const [discardDraftOpen, setDiscardDraftOpen] = useState<boolean>(false)

  const performDiscardSavedDraft = useCallback((): void => {
    clearApplyDraft(effectiveId)
    void deleteLeaveDraft().catch(() => {
      /* swallow — best effort */
    })
    setSavedDraft(null)
    setRestoreDismissed(true)
    setDraftJustSavedAt(null)
    setDraftLeaveId(null)
    notify('Draft discarded', 'success')
  }, [effectiveId, notify])

  const handleDiscardSavedDraft = useCallback((): void => {
    // Two entry points use this callback:
    //   1) The restore banner Discard button — the user is staring at
    //      a banner that already says "Restore or discard?". A second
    //      modal would feel like a stutter, so we go straight through.
    //   2) The rail "Discard saved draft" link — that's an explicit
    //      destructive action and benefits from a confirm step.
    // We distinguish via `restoreDismissed`: false means the banner is
    // visible (path 1), true means the rail link was clicked (path 2).
    if (restoreDismissed) {
      setDiscardDraftOpen(true)
      return
    }
    performDiscardSavedDraft()
  }, [restoreDismissed, performDiscardSavedDraft])

  const showRestoreBanner =
    savedDraft !== null && !restoreDismissed && draftJustSavedAt === null

  const submitDisabledReason = !canSubmit
    ? isImpersonating
      ? 'Apply is read-only while Viewing As is active.'
      : !form.type
      ? 'Select a leave type to continue.'
      : reasonBlocks
        ? 'Add a reason before submitting.'
        : docsBlocks
          ? 'A supporting document is required for this leave type.'
          : replacementExceedsCap && replacementCap
              ? `Replacement leave is capped at ${formatCapDays(
                  replacementCap.remainingDays,
                )} day(s). Reduce your selection.`
              : replacementAfterExpiry && replacementCap?.nearestExpiry
                ? `Your replacement grant expires ${replacementCap.nearestExpiry}. Pick an end date on or before that day.`
                : hasErr
                ? // Surface the actual blocking error (e.g. blackout_blocked,
                  // unpaid_not_allowed, balance_exceeded) so the reason next to
                  // the disabled button matches the gate that fired, rather than
                  // a generic "date errors" message. (Capacity is not a blocker.)
                  (blockingErrorMessage ?? 'Fix the errors above before submitting.')
                : !draft
                  ? 'Select dates and fill in the required fields.'
                  : aiProcessing
                    ? 'Checking your request — give it a second…'
                    : undefined
    : undefined

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (!canSubmit || !draft) return
    // RFC 007 — every request goes through the single `/leaves` path. Annual is
    // hard-capped: an over-balance annual request is blocked at submit
    // (`balance_exceeded`), so nothing overflows into Unpaid. Unpaid / blackout
    // leaves land PENDING and the manager escalates them to the Exec Director.
    submitMutation.mutate(draft)
  }

  function handleType(next: LeaveTypeId): void {
    setBookingWindowError(null)
    setPerDateHalfDays({})

    // Every type starts with empty dates for the employee to pick — including
    // Birthday (its calendar is constrained to the employee's birth month, so
    // they still can't land on an invalid date).
    setForm((prev) => ({
      ...prev,
      type: next,
      startDate: '',
      endDate: '',
      startHalf: 'full',
      endHalf: 'full',
    }))
    // Manual type change clears any prior auto-fill stamps.
    setAutoFilledKeys((prev) => ({ ...prev, type: false, dates: false }))
  }

  function handleRange(from: string, to: string, pick?: FixedDurationPick): void {
    setBookingWindowError(null)
    setPerDateHalfDays({})
    // Manual date pick clears the auto-fill stamp on dates.
    setAutoFilledKeys((prev) => ({ ...prev, dates: false }))

    const singleSelect = isBirthday || singleDayOnly

    // First click after reopening onto an existing selection (range types
    // only): start a FRESH 1-day pick at the clicked date rather than
    // extending the committed range. `to` is the clicked date when the picker
    // tried to extend (from stays the old start); it equals `from` on a plain
    // 1-day click. Keep the popover open so the user can still extend or set
    // AM/PM. Single-select types don't extend, and fixed-duration types place
    // start vs end by position (RangeCalendar), so both skip this.
    if (datesReopenFreshPick) {
      setDatesReopenFreshPick(false)
      if (!isFixedDuration && !singleSelect) {
        const clicked = to !== '' ? to : from
        setForm((prev) => ({
          ...prev,
          startDate: clicked,
          endDate: clicked,
          startHalf: 'full',
          endHalf: 'full',
        }))
        return
      }
    }

    // Fixed-duration types arrive with the end already pre-filled (and capped
    // at the booking window) by RangeCalendar, so from/to are taken as-is.
    setForm((prev) => ({
      ...prev,
      startDate: from,
      endDate: to,
      startHalf: 'full',
      endHalf: 'full',
    }))
    // Auto-close the popover once the selection is complete, so the user
    // doesn't need a separate "Done" click:
    //   - fixed-duration (maternity / paternity): the start click pre-fills the
    //     end but stays open so the user can move it; a click that moves the
    //     end completes the pick → close.
    //   - single-select (birthday / balance-of-1): one click IS the whole
    //     selection → close.
    //   - normal range: the FIRST click sets a 1-day range (kept open so the
    //     user can extend or set AM/PM); completion is either a SECOND click on
    //     a LATER date (multi-day, to > from) OR a re-click on the same,
    //     already-selected single day (confirming "just this one day") → close.
    const reclickSameDay =
      from !== '' &&
      from === to &&
      form.startDate === from &&
      form.endDate === to
    const complete = isFixedDuration
      ? pick === 'end'
      : singleSelect
        ? Boolean(from)
        : Boolean(from && to && to > from) || reclickSameDay
    if (complete) setDatesOpen(false)
  }

  // Wipes the current date selection back to empty (same resets as a manual
  // pick) so the user can start the range over without re-tapping the start.
  function handleClearDates(): void {
    setBookingWindowError(null)
    setPerDateHalfDays({})
    setAutoFilledKeys((prev) => ({ ...prev, dates: false }))
    setForm((prev) => ({
      ...prev,
      startDate: '',
      endDate: '',
      startHalf: 'full',
      endHalf: 'full',
    }))
  }

  const startEndpointLabel = startDate ? format(parseISO(startDate), 'EEE d MMM') : ''
  const endEndpointLabel = endDate ? format(parseISO(endDate), 'EEE d MMM') : ''
  const isMultiDay =
    startDate !== '' && endDate !== '' && endDate > startDate && !isBirthday

  // Rail stats — derived from preview + balance. Counts of teammate /
  // blackout warnings drive the CONFLICTS / BLACKOUT tiles.
  const teammateBucketCodes: ReadonlySet<string> = useMemo(
    () =>
      new Set([
        'capacity_over',
        'small_team_overlap',
        'teammate_overlap_info',
        'coverage_concern_for_health_leave',
      ]),
    [],
  )
  const conflictsCount = previewWarnings.filter((w) =>
    teammateBucketCodes.has(w.code as string),
  ).length
  const blackoutCount = previewWarnings.filter(
    (w) => (w.code as string) === 'blackout_overlap',
  ).length
  const entitlementNum = Number(balance?.entitlement ?? 0) || 0
  const availableNum = Number(balance?.available ?? 0) || 0
  const balanceAfter = Math.max(0, Number((availableNum - computedDays).toFixed(1)))

  const railStats: ReviewRailStat[] = [
    { label: 'Days', value: computedDays },
    {
      label: 'Balance after',
      value: balanceAfter,
      ...(entitlementNum > 0 ? { hint: `of ${entitlementNum}` } : {}),
    },
    { label: 'Conflicts', value: conflictsCount },
    { label: 'Blackout', value: blackoutCount },
  ]

  // ── Approver path ─────────────────────────────────────────────────────────
  // Default: the requester's direct manager decides. When the selected
  // window overlaps a blackout, the chain escalates per the blackout's
  // `level`:
  //   head           → HOD decides;  Manager informed.
  //   director       → Director decides;  Manager informed.
  //   exec_director  → Exec Director decides;  Manager informed.
  // We pick the highest-level overlapping blackout so a director-level
  // window always wins over a head-level one if both happen to overlap.
  // The applicant's own escalation routing is always scoped to their own team,
  // mirroring the server gate — an engineering-only blackout must not route an
  // HR applicant to the Exec Director just because HR can *see* it on the
  // calendar. (Calendar hatching keeps that HR-wide visibility; see
  // `blackoutDates`.) See _pickOverlappingBlackout for the shared predicate.
  const overlappingBlackout = useMemo(() => {
    if (!startDate || !endDate || !blackoutsQuery.data) return null
    return _pickOverlappingBlackout(blackoutsQuery.data, {
      startDate,
      endDate,
      teamId: user?.teamId,
    })
  }, [startDate, endDate, blackoutsQuery.data, user?.teamId])

  // Client-side block prediction. The debounced server preview lags a date
  // change by ~500ms (and React Query serves the stale result meanwhile), so
  // without this the rail flashes the ESCALATE state — Exec-Director chip +
  // advisory rows — before flipping to the block. The flag-driven gates
  // (blackout on Annual/Unpaid, unpaid-not-allowed) are fully knowable on the
  // client: we already have the blackout locally and the gate flags are
  // mirrored to the web. So predict the block immediately and let the server
  // preview (blockingErrorMessage) confirm/override once it lands. Capacity /
  // balance blocks stay server-only (blockingErrorMessage). The strings mirror
  // the api rule engine (R6 / R7) — keep them in sync.
  const predictedBlockMessage = useMemo((): string | undefined => {
    if (form.type === 'unpaid' && !flags.allowUnpaidApplication) {
      return 'Unpaid leave is not currently accepted. Please contact HR if you need time off beyond your paid balance.'
    }
    if (
      overlappingBlackout != null &&
      _DISCRETIONARY_GATED_TYPES.has(form.type) &&
      !flags.blackoutGateEscalate
    ) {
      return `Blackout period: ${overlappingBlackout.name}. Leave cannot be filed for these dates.`
    }
    return undefined
  }, [form.type, overlappingBlackout])

  // Server preview is authoritative. The local prediction ONLY fills the gap
  // while the debounced preview is still in flight for the current draft — so
  // it kills the escalate→block flash without ever overriding a settled server
  // result (e.g. a flag-ON environment where the server allows + escalates).
  const effectiveBlockMessage =
    blockingErrorMessage ?? (previewPending ? predictedBlockMessage : undefined)
  const isBlocked = effectiveBlockMessage !== undefined

  // The reason is pointless to fill when there are no dates yet OR the request
  // is hard-blocked (it can't be filed regardless of what's written). Disable
  // the field in both cases.
  const reasonDisabled = reasonLocked || isBlocked

  const approverPath: ApproverPathStep[] = useMemo(() => {
    const you: ApproverPathStep = {
      name: 'You',
      role: 'employee',
      decides: false,
    }
    // Prefer preview's managerName once dates are picked (authoritative
    // — the rule engine has already resolved reportsTo); fall back to
    // the approver-chain query so the rail shows the manager chip
    // immediately on page load, before any date selection.
    const chain = approverChainQuery.data
    const managerName =
      previewQuery.data?.managerName ?? chain?.manager?.name ?? null
    // Escalation tier from leave type + blackout. A literal unpaid type is
    // manager-recommends → Director-decides (policy §4.3); a blackout overlap
    // escalates too. (Annual no longer overflows into unpaid — an over-balance
    // annual request is blocked at submit, so there is no credit-overflow tier.)
    //
    // When the request is HARD-blocked (blackout_blocked / unpaid_not_allowed
    // while the gate flag is OFF), nothing gets approved — so the escalation is
    // not true. Fall back to the base manager path and drop the Exec-Director
    // rung. (Capacity no longer blocks or escalates.)
    const escalationRole = isBlocked
      ? null
      : _resolveEscalationRole(form.type, overlappingBlackout)
    if (escalationRole === null) {
      return [
        you,
        {
          name: managerName ?? APPROVER_ROLE_LABEL.manager,
          role: 'manager',
          decides: true,
        },
      ]
    }
    // Resolve the Executive Director's actual name from the chain so the
    // chip reads "You → Adila Rahman" rather than the generic role label.
    // Falls back to the role label when the org chart hasn't populated the
    // Exec Director rung yet.
    const escalationName =
      chain?.execDirector?.name ?? APPROVER_ROLE_LABEL.exec_director
    // Escalated path is three rungs: the request goes to the manager to
    // ENDORSE (they don't decide), who passes it up to the final
    // decider. Showing the manager as an explicit intermediate makes the
    // "endorse upward" routing legible instead of collapsing to
    // You → <final approver>.
    return [
      you,
      {
        name: managerName ?? APPROVER_ROLE_LABEL.manager,
        role: 'manager',
        decides: false,
        qualifier: 'for endorsement',
      },
      {
        name: escalationName,
        role: escalationRole,
        decides: true,
      },
    ]
  }, [
    isBlocked,
    overlappingBlackout,
    form.type,
    previewQuery.data?.managerName,
    approverChainQuery.data,
  ])

  const approverNote = useMemo((): {
    text: string
    severity: 'advisory' | 'blocking'
  } | null => {
    // A hard block has no approval flow — suppress the escalation note (the
    // block reason is shown in the "Things to know" panel instead).
    if (isBlocked) return null
    const escalationRole = _resolveEscalationRole(
      form.type,
      overlappingBlackout,
    )
    if (escalationRole === null) return null
    const roleLabel = APPROVER_ROLE_LABEL[escalationRole]
    // Plain-language reason for the escalation: name what triggered it
    // (unpaid leave type / blackout overlap) and where it now routes. The
    // checklist states the facts; this owns "who decides and why".
    const isUnpaidType = form.type === 'unpaid'
    const reason = isUnpaidType ? 'Unpaid leave' : 'A blackout overlap'
    return {
      text: `${reason}, so your approver endorses it up to the ${roleLabel} for final sign-off.`,
      severity: isUnpaidType ? 'blocking' : 'advisory',
    }
  }, [
    isBlocked,
    overlappingBlackout,
    form.type,
  ])

  // ── "Before you submit" advisory checklist (rail) ──────────────────────────
  // Four fixed rows derived from data already on the page. Facts only — the
  // ✓ / △ glyph carries the status; escalation ("routes to the Director")
  // lives in the approver note above, not here.
  const leaveCheckReady = Boolean(startDate && endDate) && computedDays > 0
  // Never show a negative balance; an over-drawn balance reads as 0 left.
  const balanceLeft = Math.max(0, availableNum)
  // Coverage: reuse the rule engine's own teammate warning — its count is the
  // authoritative distinct-teammate figure — and flag △ only when the request
  // would push the team OVER its capacity cap (capacity_over), not merely
  // because someone is out.
  const teammateWarning = previewWarnings.find((w) =>
    teammateBucketCodes.has(w.code as string),
  )
  const coverageOverCap = previewWarnings.some(
    (w) => (w.code as string) === 'capacity_over',
  )
  // Team coverage is only actionable for the discretionary leaves the employee
  // controls the timing of — Annual, Unpaid, Birthday and Replacement. These
  // are exactly the backend's _DISCRETIONARY_GATED_TYPES (leave_rules.py R8):
  // the server computes the over-cap warning (capacity_over /
  // teammate_overlap_info) for all four, so surfacing the row for them keeps the
  // rail in sync. Everything else (statutory / unplanned / event-bound) can't be
  // shifted, so the coverage row would be noise.
  const COVERAGE_TYPES: ReadonlySet<string> = new Set([
    'annual',
    'unpaid',
    'birthday',
    'replacement',
  ])
  const showCoverage = !!form.type && COVERAGE_TYPES.has(form.type)
  // Pattern: the on-the-fly preview computed for THIS draft + leave type
  // (public.pattern_catalog). Two distinct questions, surfaced as two clauses
  // — the SAME model the leave-detail page uses, so the two surfaces never
  // disagree:
  //   • activeWithoutDraft → "You're already in X" (today-anchored baseline,
  //     matching the Leave Rhythm card / health bar).
  //   • triggeredByDraft → "Filing this will trigger Y" (the draft's own days
  //     push it over a threshold it wasn't already past).
  // Kept separate so a well-planned request isn't blamed for a flag the
  // employee already carries.
  const patternHits = previewQuery.data?.patterns ?? []
  const activePatternHits = patternHits.filter((h) => h.activeWithoutDraft)
  const triggeredPatternHits = patternHits.filter((h) => h.triggeredByDraft)
  // Join pattern names for the row, stripping any trailing "pattern" word from
  // the label (e.g. "Long-weekend pattern") so the sentence's own "pattern(s)"
  // suffix doesn't double up. 3+ names take an Oxford comma ("a, b, and c").
  const joinPatternNames = (hits: ReadonlyArray<{ displayName: string }>): string => {
    const names = hits.map((h) => h.displayName.toLowerCase().replace(/\s*pattern$/i, '').trim())
    if (names.length <= 1) return names[0] ?? ''
    if (names.length === 2) return `${names[0]} and ${names[1]}`
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
  }
  // Leave types that have ANY pattern in public.pattern_catalog (the union of
  // the seed's relevant_leave_types). Types outside this set have nothing to
  // check, so the pattern row is hidden — no misleading "rhythm looks steady".
  // Replacement is included because the long-weekend pattern now scopes it (its
  // catalog relevant_leave_types gained 'replacement' — see migration 0166); a
  // last-minute replacement day is the same Mon/Fri long-weekend signal.
  const PATTERN_RELEVANT_TYPES: ReadonlySet<string> = new Set([
    'medical',
    'annual',
    'unpaid',
    'replacement',
  ])
  const showPattern = !!form.type && PATTERN_RELEVANT_TYPES.has(form.type)
  // Date-independent heads-up rows — render in the "Before you submit" box even
  // before dates are picked (the check itself is date-gated, but this is about
  // the leave-type choice). Today: the unpaid notice. Consolidated here (rather
  // than a standalone amber block) at HR's request. Deliberately terse — just
  // the payroll consequence; no policy chip, no escalation detail (that's the
  // approver path's job), and no nudge toward other leave types.
  const leaveCheckPersistentItems: LeaveCheckItem[] =
    form.type === 'unpaid'
      ? [
          {
            key: 'unpaid',
            status: 'attn' as const,
            fact: <>You won&apos;t be paid for these days (affects payroll).</>,
          },
        ]
      : []
  const yearEndDaysLeft =
    isFixedDuration && fixedDuration
      ? daysLeftAfterCap({
          durationDays: fixedDuration,
          from: form.startDate,
          to: form.endDate,
          maxDate: bookingWindow?.maxDate,
        })
      : null
  const leaveCheckItems: LeaveCheckItem[] = [
    {
      key: 'balance',
      status: 'ok',
      // Unpaid draws no balance, so "N left" is meaningless — state the
      // requested duration as unpaid days instead. Every other type shows the
      // remaining-vs-requested comparison.
      fact:
        form.type === 'unpaid' ? (
          <>
            <strong style={{ color: 'var(--text)' }}>{computedDays}</strong>{' '}
            {computedDays === 1 ? 'day' : 'days'} unpaid leave requested
          </>
        ) : (
          <>
            <strong style={{ color: 'var(--text)' }}>
              {balanceLeft} {balanceLeft === 1 ? 'day' : 'days'}
            </strong>{' '}
            left, <strong style={{ color: 'var(--text)' }}>{computedDays}</strong> requested
          </>
        ),
    },
    // Year-end row — a maternity / paternity block cut short by the booking
    // window's 31 Dec cap. The rest is filed next year against the new year's
    // credit; say how many days, so the follow-up asks for the remainder only.
    ...(yearEndDaysLeft !== null
      ? [
          {
            key: 'year-end-cap',
            status: 'attn' as const,
            fact: (
              <>
                File the remaining{' '}
                <strong style={{ color: 'var(--text)' }}>
                  {yearEndDaysLeft} {yearEndDaysLeft === 1 ? 'day' : 'days'}
                </strong>{' '}
                next year, once your leave credit is refreshed.
              </>
            ),
          },
        ]
      : []),
    // Emergency row — short-notice annual/replacement leave. Un-gated by leave
    // type (server's is_emergency decides); reuses the rule-engine message so
    // the balance wording stays correct per type. Non-blocking heads-up.
    ...(isEmergencyDraft
      ? [
          {
            key: 'emergency',
            status: 'attn' as const,
            fact: <strong style={{ color: 'var(--text)' }}>Emergency Leave</strong>,
          },
        ]
      : []),
    // Proof row — only for leave types that require a supporting document.
    // △ until the file is attached (Submit is blocked without it anyway).
    ...(form.type && docsRequired
      ? [
          {
            key: 'proof',
            status: stagedFiles.length > 0 ? ('ok' as const) : ('attn' as const),
            fact:
              stagedFiles.length > 0
                ? `${capitaliseFirst(evidenceLabelFor(form.type))} attached.`
                : `${capitaliseFirst(evidenceLabelFor(form.type))} required — attach it before you submit.`,
          },
        ]
      : []),
    ...(showCoverage
      ? [
          {
            key: 'coverage',
            status: coverageOverCap ? ('attn' as const) : ('ok' as const),
            fact: teammateWarning
              ? teammateWarning.message
              : 'No teammates out on your dates.',
          },
        ]
      : []),
    {
      key: 'blackout',
      status: overlappingBlackout ? 'attn' : 'ok',
      fact: overlappingBlackout ? (
        <>
          Overlaps <strong style={{ color: 'var(--text)' }}>{overlappingBlackout.name}</strong>
        </>
      ) : (
        'No blackout overlap.'
      ),
    },
    ...(showPattern && form.type
      ? [
          {
            key: 'pattern',
            status: patternHits.length > 0 ? ('attn' as const) : ('ok' as const),
            fact:
              patternHits.length > 0 ? (
                <>
                  {activePatternHits.length > 0 ? (
                    <>
                      You&apos;re already in the{' '}
                      <strong style={{ color: 'var(--text)' }}>
                        {joinPatternNames(activePatternHits)}
                      </strong>{' '}
                      pattern{activePatternHits.length > 1 ? 's' : ''}.
                    </>
                  ) : null}
                  {activePatternHits.length > 0 && triggeredPatternHits.length > 0 ? ' ' : null}
                  {triggeredPatternHits.length > 0 ? (
                    <>
                      Filing this will trigger the{' '}
                      <strong style={{ color: 'var(--text)' }}>
                        {joinPatternNames(triggeredPatternHits)}
                      </strong>{' '}
                      pattern{triggeredPatternHits.length > 1 ? 's' : ''}.
                    </>
                  ) : null}
                </>
              ) : (
                // Steady message is scoped to the type being filed.
                `Your ${LEAVE_TYPE_LABEL[form.type].toLowerCase()} leave rhythm looks steady.`
              ),
          },
        ]
      : []),
  ]

  const saveDraftLabel = draftJustSavedAt
    ? `Saved ${formatRelativeTime(draftJustSavedAt)}`
    : 'Save draft'

  // Dates step hint — a plain tap hint. Blackout-window and advance-booking
  // notes are surfaced in the "Things to know" checklist instead.
  const datesHint: ReactNode = isBirthday
    ? 'Tap a date'
    : isFixedDuration
      ? 'Tap a start date, we fill in the end date'
      : 'Tap a start date, then an end date'

  return (
    <div className="page" data-testid="apply-leave">
      {ConfettiPortal}
      {isBirthday ? <span data-testid="birthday-confetti-anchor" aria-hidden style={{ display: 'none' }} /> : null}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15, margin: 0 }}>
          Apply for leave
        </h1>
      </div>

      {showRestoreBanner && savedDraft ? (
        <FadeIn duration="base">
          <Card padding={14} data-testid="restore-draft-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text-2)', minWidth: 0 }}>
                You have a saved draft from{' '}
                <strong style={{ color: 'var(--text)' }}>
                  {formatRelativeTime(savedDraft.savedAt)}
                </strong>
                . Restore it?
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  data-testid="restore-draft"
                  onClick={handleRestoreDraft}
                >
                  Restore
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  size="md"
                  data-testid="discard-draft"
                  onClick={handleDiscardSavedDraft}
                >
                  Discard
                </Button>
              </div>
            </div>
          </Card>
        </FadeIn>
      ) : null}

      {isImpersonating ? (
        <FadeIn rise={4} duration="base">
          <Card padding={14}>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
              Apply is read-only while you're using <strong>Viewing As</strong>. Stop
              impersonating in the top bar to submit a real request.
            </span>
          </Card>
        </FadeIn>
      ) : null}


      <form
        onSubmit={handleSubmit}
        style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'minmax(0, 1.4fr) minmax(0, 1fr)' : '1fr',
          gap: isDesktop ? 16 : 14,
          alignItems: 'start',
        }}
      >
        {/* Left column — the request form. Submit / Cancel / Save draft now
            live as a right-aligned footer row INSIDE this card, after Reason,
            so the form reads top-to-bottom and commits at its own
            bottom-right edge rather than in the right rail. */}
        <div
          style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 14, boxShadow: 'var(--shadow-sm)', minWidth: 0, overflow: 'hidden',
          }}
          data-testid="new-request-card"
        >
          {/* Card header — the bottom border doubles as a completion track,
              colouring in as the required inputs get satisfied (type → dates →
              reason, plus document upload when the leave type needs it). */}
          <div style={{ position: 'relative', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-0.005em' }}>New request</span>
            <ApplyProgressBar completed={progressCompleted} total={progressTotal} />
          </div>

          {/* Card body — wrapped in AnimatedHeight so the whole "New
              request" card eases its height when sections appear/disappear
              (SmartIntake on type change, half-day rows on dates picked,
              fixed-duration / booking-window / replacement / unpaid notices,
              maternity delivery field, reason assessment, etc.) instead of
              snapping. The inner flex column preserves the original layout. */}
          <AnimatedHeight duration="base">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LeaveTypePicker
            value={form.type}
            onChange={handleType}
            balances={balances}
            gender={user?.gender ?? null}
            onProbation={employeeProfileQuery.data?.onProbation}
            leaveTypesData={leaveTypesQuery.data}
            autoFilled={autoFilledKeys.type}
            unavailableTypes={unavailableLeaveTypes}
          />
          {/* Document upload sits directly under the leave type so it
               can autofill dates + reason before the user touches the calendar.
               Matches the Lumen prototype's vertical order. */}
          {docsRequired && form.type ? (
            <FadeIn rise={4} duration="base">
              <StagedDocumentPicker
                files={stagedFiles}
                onChange={(next) => {
                  // Removing the staged document also clears the form fields
                  // that were auto-filled from it, plus the AI panels hanging
                  // off them. Without this the user is left staring at
                  // half-filled state they didn't type.
                  if (next.length === 0 && stagedFiles.length > 0) {
                    setForm((prev) => ({
                      ...prev,
                      ...(autoFilledKeys.dates
                        ? { startDate: '', endDate: '' }
                        : {}),
                      ...(autoFilledKeys.reason ? { reason: '' } : {}),
                      startHalf: 'full',
                      endHalf: 'full',
                    }))
                    setPerDateHalfDays({})
                    setAutoFilledKeys({
                      type: false,
                      dates: false,
                      reason: false,
                    })
                    setReasonQualityError(null)
                  }
                  setStagedFiles(next)
                }}
                disabled={submitMutation.isPending}
              />
            </FadeIn>
          ) : null}

          {/* Discreet unpaid-leave escape hatch. `unpaid` is deliberately kept
               OUT of the LeaveTypePicker dropdown (allowed, but not obvious) —
               this muted link is the only entry point. Hidden once unpaid is
               already selected (the warning below takes over) and gated behind
               the `allowUnpaidApplication` flag. Selecting it reuses handleType,
               so the rest of the form (warning, Exec-Director escalation,
               coverage) engages exactly as for any other type. */}
          {flags.allowUnpaidApplication && form.type !== 'unpaid' && annualExhausted ? (
            <button
              type="button"
              data-testid="unpaid-escape-hatch"
              onClick={() => handleType('unpaid')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 2px',
                background: 'none',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 12,
                color: 'var(--text-3)',
                cursor: 'pointer',
                textAlign: 'left',
                lineHeight: 1.4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--text-2)'
                e.currentTarget.style.textDecoration = 'underline'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-3)'
                e.currentTarget.style.textDecoration = 'none'
              }}
            >
              Need time beyond your leave balance? Request unpaid leave →
            </button>
          ) : null}
        </div>

        {/* Unpaid heads-up moved into the "Before you submit" checklist (the
            `unpaid` row in leaveCheckItems) — no standalone amber block here. */}

        {form.type === 'replacement' &&
        replacementCap !== null &&
        replacementCap.remainingDays <= 0 ? (
          <FadeIn rise={4} duration="base">
          <div
            data-testid="replacement-empty-state"
            style={{
              margin: '0 18px',
              marginBottom: '18px',
              padding: 12,
              borderRadius: 10,
              background: 'var(--info-bg)',
              border: '1px solid var(--info-line)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Icon
                name="info"
                size={20}
                color="var(--info-fg)"
                aria-hidden="true"
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--info-fg)',
                    marginBottom: 4,
                  }}
                >
                  No replacement leave balance yet
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: 'var(--text-2)',
                    lineHeight: 1.5,
                  }}
                >
                  You earn Replacement Leave by working on a gazetted public
                  holiday or a company-mandated working day, then logging it
                  with HR. Until that's recorded, this type can't be applied.
                  Pick another type or contact HR.
                </p>
              </div>
            </div>
          </div>
          </FadeIn>
        ) : null}

        <div style={{ padding: 18 }} data-testid="step-dates-card">
          <StepHeader
            label="Dates"
            required
            hint={datesHint}
            adornment={autoFilledKeys.dates ? <AutoFilledTag /> : null}
          />

          {/* Dates are picked in a popover behind this trigger — collapsing
              the two-month calendar keeps the form narrow. The trigger is
              disabled until a leave type is chosen and shows a live summary
              of the selection ("6 Aug – 11 Aug 2026 · 4 working days"). */}
          <div ref={datesPopoverRef} style={{ position: 'relative' }}>
            <button
              type="button"
              data-testid="dates-trigger"
              disabled={!form.type || balanceExhausted}
              onClick={() => {
                // Opening onto an existing selection → the next click is a
                // fresh re-pick, not a range extension.
                if (!datesOpen && form.startDate) setDatesReopenFreshPick(true)
                setDatesOpen((o) => !o)
              }}
              aria-haspopup="dialog"
              aria-expanded={datesOpen}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                minHeight: 44,
                background:
                  form.type && !balanceExhausted ? 'var(--surface)' : 'var(--surface-2)',
                border: `1px solid ${datesOpen ? 'var(--steel-blue)' : 'var(--border-strong)'}`,
                boxShadow: datesOpen ? '0 0 0 3px var(--baby-blue-tint)' : 'none',
                borderRadius: 10,
                cursor: form.type && !balanceExhausted ? 'pointer' : 'not-allowed',
                opacity: form.type && !balanceExhausted ? 1 : 0.6,
                fontFamily: 'inherit',
                fontSize: 13.5,
                color: 'var(--text)',
                textAlign: 'left',
                transition: 'border-color .12s, box-shadow .12s',
              }}
            >
              <Icon name="calendar_month" size={18} color="var(--text-3)" aria-hidden="true" />
              {datesRangeLabel ? (
                <span style={{ fontWeight: 500 }}>
                  {datesRangeLabel}
                  {datesDaysLabel ? (
                    <>
                      {' · '}
                      <strong style={{ fontWeight: 700 }}>{datesDaysLabel}</strong>
                    </>
                  ) : null}
                </span>
              ) : (
                <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>
                  {!form.type
                    ? 'Select a leave type first'
                    : balanceExhausted
                      ? `No ${LEAVE_TYPE_LABEL[form.type]} balance left`
                      : 'Set dates'}
                </span>
              )}
              <span
                style={{
                  marginLeft: 'auto',
                  color: 'var(--text-3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  transition: 'transform .15s',
                  transform: datesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <Icon name="expand_more" size={16} aria-hidden="true" />
              </span>
            </button>

            {datesOpen ? (
              // Inline expand (not an overlay): the card sets `overflow:hidden`
              // for its rounded corners + height animation, which would clip
              // an absolutely-positioned popover. Dropping the calendar in-flow
              // lets the parent AnimatedHeight ease the card taller instead.
              <FadeIn rise={4} duration="fast">
              <div
                role="group"
                aria-label="Pick dates"
                data-testid="dates-popover"
                style={{
                  marginTop: 8,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                {/* Fixed-duration types (maternity / paternity) pre-fill the
                    end from the start (capped at the booking window). Say so
                    up front, and that a later tap moves the end, so the
                    pre-filled range isn't read as locked. */}
                {isFixedDuration && fixedDuration && form.type ? (
                  <div
                    data-testid="fixed-duration-hint"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      marginBottom: 10,
                      padding: '8px 10px',
                      background: 'var(--baby-blue-tint)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 12.5,
                      color: 'var(--text-2)',
                    }}
                  >
                    <Icon name="info" size={16} color="var(--steel-blue)" aria-hidden="true" />
                    <span>
                      {LEAVE_TYPE_LABEL[form.type]} is up to{' '}
                      <strong>{fixedDuration} days</strong>. Pick your{' '}
                      <strong>start date</strong> and we&rsquo;ll fill in the end
                      date. Tap a later date to change the end.
                    </span>
                  </div>
                ) : null}
                <RangeCalendar
                  from={form.startDate}
                  to={form.endDate}
                  onChange={handleRange}
                  publicHolidays={publicHolidayDates}
                  holidayNames={holidayNames}
                  blackoutDates={blackoutDates}
                  hideBlackoutLegend
                  single={isBirthday || singleDayOnly}
                  oneClickSingleDay={!isBirthday && !isFixedDuration && !singleDayOnly}
                  fixedDuration={isFixedDuration ? fixedDuration : undefined}
                  mobile={!isDesktop}
                  {...(form.type ? { accent: LEAVE_TYPE_STYLE[form.type] } : {})}
                  min={birthdayMonthBounds?.min}
                  max={birthdayMonthBounds?.max ?? bookingWindow?.maxDate}
                  {...(spanCapDays !== undefined ? { maxSpanDays: spanCapDays } : {})}
                  spanCountsElapsedDays={spanCapElapsed}
                />
                {/* Completing a selection auto-closes the popover (end of a
                    multi-day range, or the single click for single-select /
                    fixed-duration types). Done stays as an explicit dismiss for
                    the in-between state — a 1-day pick the user hasn't extended
                    — and Clear resets the range. */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <Button
                    type="button"
                    variant="tertiary"
                    size="sm"
                    data-testid="dates-clear"
                    onClick={handleClearDates}
                    disabled={!form.startDate}
                  >
                    Clear
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    data-testid="dates-done"
                    onClick={() => setDatesOpen(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
              </FadeIn>
            ) : null}
          </div>
          {bookingWindowError ? (
            <FadeIn rise={4} duration="fast">
              <InlineAlert
                kind="danger"
                message={bookingWindowError}
                testId="booking-window-error"
              />
            </FadeIn>
          ) : null}

          {form.type === 'replacement' && replacementCap !== null ? (
            <FadeIn rise={4} duration="fast">
            <div
              data-testid="replacement-cap-hint"
              style={{
                marginTop: 6,
                fontSize: 12,
                color: 'var(--text-3)',
                fontStyle: 'italic',
              }}
            >
              You have{' '}
              <strong>
                {formatCapDays(replacementCap.remainingDays)} day
                {replacementCap.remainingDays === 1 ? '' : 's'}
              </strong>{' '}
              of replacement leave available
              {replacementCap.nearestExpiry
                ? ` (nearest expiry ${replacementCap.nearestExpiry})`
                : ''}
              .
            </div>
            </FadeIn>
          ) : null}

          {replacementExceedsCap && replacementCap ? (
            <FadeIn rise={4} duration="fast">
              <InlineAlert
                kind="danger"
                testId="replacement-exceeds-cap-error"
                message={`Maximum ${formatCapDays(replacementCap.remainingDays)} day${
                  replacementCap.remainingDays === 1 ? '' : 's'
                } — adjust your end date to stay within your replacement grant.`}
              />
            </FadeIn>
          ) : null}

          {replacementAfterExpiry && replacementCap?.nearestExpiry ? (
            <FadeIn rise={4} duration="fast">
              <InlineAlert
                kind="danger"
                testId="replacement-after-expiry-error"
                message={`Your replacement grant expires ${replacementCap.nearestExpiry}. Pick an end date on or before that day.`}
              />
            </FadeIn>
          ) : null}

          {isFixedDuration && fixedDuration ? (
            <FadeIn rise={4} duration="fast">
            <div
              data-testid="fixed-duration-message"
              style={{
                marginTop: 8,
                fontSize: 12,
                color: 'var(--text-3)',
                fontStyle: 'italic',
                lineHeight: 1.5,
              }}
            >
              {fixedDurationNote({
                label: form.type ? LEAVE_TYPE_LABEL[form.type] : '',
                durationDays: fixedDuration,
                from: form.startDate,
                to: form.endDate,
                maxDate: bookingWindow?.maxDate,
              })}
            </div>
            </FadeIn>
          ) : null}


          {/* Only once the calendar is closed: while it's open, a click on
              Full/AM/PM lands outside it, collapses it, and the options shift
              up before the click registers, so the pick was lost. */}
          {halfDayAllowed && !isFixedDuration && startDate && endDate && !datesOpen ? (
            <FadeIn rise={4} duration="base">
              {flags.halfDayPerDate ? (
                // Per-date mode (flag ON): full HalfDayPicker for every working day.
                <div style={{ marginTop: 10 }}>
                  <HalfDayPicker
                    startDate={startDate}
                    endDate={endDate}
                    publicHolidays={publicHolidayDates}
                    value={perDateHalfDays}
                    onChange={setPerDateHalfDays}
                    requiredHalfDates={requiredHalfDates}
                  />
                </div>
              ) : (
                // Legacy mode (flag OFF): start/end rows only.
                <div
                  style={{
                    marginTop: 10,
                    display: 'grid',
                    gridTemplateColumns: isMultiDay ? '1fr 1fr' : '1fr',
                    gap: 8,
                  }}
                >
                  <HalfDayRow
                    label="Start"
                    date={startEndpointLabel}
                    value={form.startHalf}
                    onChange={(next) =>
                      setForm((prev) => ({ ...prev, startHalf: next }))
                    }
                  />
                  {isMultiDay ? (
                    <HalfDayRow
                      label="End"
                      date={endEndpointLabel}
                      value={form.endHalf}
                      onChange={(next) =>
                        setForm((prev) => ({ ...prev, endHalf: next }))
                      }
                    />
                  ) : null}
                </div>
              )}
            </FadeIn>
          ) : null}
        </div>

        <div style={{ padding: 18 }} data-testid="step-reason-card">
          <StepHeader
            label="Reason"
            required
            {...(reasonDisabled
              ? { hint: reasonLocked ? 'Pick your dates first.' : "This request can't be filed." }
              : {})}
            adornment={autoFilledKeys.reason ? <AutoFilledTag /> : null}
          />
          {reasonQualityError ? (
            <FadeIn rise={4} duration="fast">
              <div style={{ marginBottom: 8 }}>
                <InlineAlert
                  kind="danger"
                  testId="reason-quality-error"
                  message={reasonQualityError}
                />
              </div>
            </FadeIn>
          ) : null}
          <Field
            label="Reason"
            required
            hideLabel
            htmlFor="leave-reason"
          >
            <textarea
              id="leave-reason"
              value={form.reason}
              onChange={(event) => {
                setReasonQualityError(null)
                setAutoFilledKeys((prev) =>
                  prev.reason ? { ...prev, reason: false } : prev,
                )
                setForm((prev) => ({ ...prev, reason: event.target.value }))
              }}
              rows={4}
              disabled={reasonDisabled}
              placeholder={
                reasonLocked
                  ? 'Pick your dates first…'
                  : isBlocked
                    ? "This request can't be filed…"
                    : 'e.g. Hometown visit, family event, medical appointment…'
              }
              data-testid="reason-input"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 10,
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: reasonDisabled ? 'var(--surface-2)' : 'var(--surface)',
                cursor: reasonDisabled ? 'not-allowed' : 'auto',
                opacity: reasonDisabled ? 0.6 : 1,
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: 13,
                color: 'var(--text)',
              }}
            />
          </Field>
        </div>

        {/* Legacy docs section removed — Smart Intake renders the proof
             upload at the top of the form, directly under LeaveTypePicker. */}

          {/* Actions — submit / cancel / save draft as a right-aligned footer
              row INSIDE the form card, after Reason. The right rail is purely
              informational; the primary action lands at the form's own
              bottom-right edge. Stats + approver path render in the rail, so
              both are hidden here. */}
          <ReviewRail
            inlineActions
            hideStats
            hideApproverPath
            stats={railStats}
            approverPath={approverPath}
            approverNote={approverNote}
            canSubmit={canSubmit}
            submitDisabledReason={submitDisabledReason}
            isSubmitting={submitMutation.isPending}
            celebrating={celebrating}
            saveDraftLabel={saveDraftLabel}
            onSaveDraft={handleSaveDraft}
            onCancel={() => {
              void navigate({ to: '/my-leave' })
            }}
            onDiscardDraft={handleDiscardSavedDraft}
            hasSavedDraft={savedDraft !== null}
            isImpersonating={isImpersonating}
          />
          </div>{/* end card body */}
          </AnimatedHeight>
        </div>{/* end new-request-card */}

        <aside
          data-testid="apply-rail-aside"
          style={{
            alignSelf: 'start',
          }}
        >
          {/* Aside content wrapped in AnimatedHeight so the panel column
              eases when ILMU narrative, approver path, review-rail buttons,
              or policy reminders grow/shrink — keeps the sticky offset
              calm while the left form is being edited. */}
          <AnimatedHeight duration="base">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Approver path — now at the TOP of the rail so it answers
              "who's this going to?" before ILMU weighs in. The rail reads
              top-to-bottom as: who approves → what the system thinks. */}
          <ApproverPathPanel
            approverPath={approverPath}
            approverNote={approverNote}
          />

          {/* "Before you submit" — a compact advisory checklist (balance,
              team coverage, blackout, pattern), replacing the dark ILMU
              Leave Assistant panel (2026-07-16). Facts only; each row is a
              ✓ / △. "Who decides" (escalation) is the approver note above. */}
          <LeaveCheck
            items={leaveCheckItems}
            ready={leaveCheckReady}
            persistentItems={leaveCheckPersistentItems}
            {...(effectiveBlockMessage
              ? { blockMessage: effectiveBlockMessage }
              : {})}
          />

          </div>
          </AnimatedHeight>
        </aside>
      </form>

      <ConfirmationDialog
        open={replaceDraftOpen}
        onClose={() => setReplaceDraftOpen(false)}
        onConfirm={() => {
          setReplaceDraftOpen(false)
          void performSaveDraft()
        }}
        title="Replace existing draft?"
        subtitle={
          savedDraft
            ? `You already have a draft saved ${formatRelativeTime(savedDraft.savedAt)}. Saving will replace it.`
            : 'You already have a saved draft. Saving will replace it.'
        }
        confirmLabel="Replace draft"
        cancelLabel="Keep existing"
        variant="danger"
        data-testid="replace-draft-confirm"
      />

      <ConfirmationDialog
        open={discardDraftOpen}
        onClose={() => setDiscardDraftOpen(false)}
        onConfirm={() => {
          setDiscardDraftOpen(false)
          performDiscardSavedDraft()
        }}
        title="Discard saved draft?"
        subtitle={
          savedDraft
            ? `You'll lose the draft from ${formatRelativeTime(savedDraft.savedAt)}. This can't be undone.`
            : "You'll lose the saved draft. This can't be undone."
        }
        confirmLabel="Discard"
        cancelLabel="Keep draft"
        variant="danger"
        data-testid="discard-draft-confirm"
      />
    </div>
  )
}
